import {
  React,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import EnhancedImageLightbox from "../EnhancedImageLightbox";

import ThemeContext from "../../lib/theme-context";
import { DataFetcher } from "../../utils/dataFetcher";
import throwMessage from "../../utils/throwMessage";
import { getCurrencyForAccount } from "../../utils/currencyUtils";

import moment from "moment-timezone";

import CustomMultiTextField from "../textfields/multiRow_standard";
import VoiceToText from "../VoiceToText";
import StarRating from "../StarRating";

import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Grid from "@mui/material/Grid";
import CustomSelect from "../select/index";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import AuthContext from "../../lib/auth-context";

import { useLongPress } from "use-long-press";

import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/Share";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

import Tooltip from "@mui/material/Tooltip";

import ConfirmDialog from "../modals/confirm-dialog";

import useWindowDimensions from "../../utils/width";

const QUESTION_CUTOFF_DATE = new Date("2026-05-17T23:59:59");

// Helper function to convert date/time to selected timezone
const formatDateTimeInTimezone = (dateStr, timeStr, originalTimezone) => {
  if (!dateStr) return "";

  try {
    // Get the user's selected timezone from settings
    const storedTimezone = localStorage.getItem("economic-calendar-timezone");
    const targetTimezone =
      storedTimezone && storedTimezone !== "local"
        ? storedTimezone
        : Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Parse the date/time in the original timezone the trade was taken in
    const dateTimeStr = timeStr ? `${dateStr} ${timeStr}` : dateStr;
    const originalMoment = moment.tz(
      dateTimeStr,
      "DD/MM/YY HH:mm",
      originalTimezone || "UTC",
    );

    // Convert to the selected timezone
    const convertedMoment = originalMoment.clone().tz(targetTimezone);

    // Format for display
    const formattedDate = convertedMoment.format("DD/MM/YY");
    const formattedTime = convertedMoment.format("HH:mm");

    return timeStr ? `${formattedDate} ${formattedTime}` : formattedDate;
  } catch (error) {
    console.error("Error converting timezone:", error);
    // Fallback to original values if conversion fails
    return timeStr ? `${dateStr} ${timeStr}` : dateStr;
  }
};

const getJournalQuestions = (isNewQuestions) => {
  if (isNewQuestions) {
    return {
      question_1:
        "Did I strictly follow my trading plan? Where did I deviate?",
      question_2:
        "If this exact same setup appears tomorrow, is it system-appropriate to take again, and why?",
      question_3:
        "What specific emotions did I battle before entry, while active, and after exit?",
      question_4:
        "What is the single biggest lesson this trade highlighted that I need to remember for the future?",
    };
  } else {
    return {
      question_1: "What were the pros ?",
      question_2: "What were the cons ?",
      question_3: "What emotions did I feel ?",
      question_4: "Would I take this trade again ?",
      question_5: "Did I follow my plan ?",
      question_6: "Anything to add ?",
    };
  }
};

const imgStyle = {
  width: "100%",
  height: "430px",
  objectFit: "cover",
  borderRadius: "10px",
  cursor: "pointer",
};

const Journal = ({ open, onClose, calculateFields, calculateOutcome }) => {
  const { width } = useWindowDimensions();

  const theme = useContext(ThemeContext);
  const auth = useContext(AuthContext);
  const [usdPair, setUsdPair] = useState(undefined);
  const [takeAgain, setTakeAgain] = useState("");
  const [pairSelected, setPairSelected] = useState(undefined);
  const [pros, setPros] = useState("");
  const [cons, setCons] = useState("");
  const [emotions, setEmotions] = useState("");
  const [plan, setPlan] = useState("");
  // New question sub-sections
  const [executionRight, setExecutionRight] = useState("");
  const [executionDeviate, setExecutionDeviate] = useState("");
  const [systemAppropriate, setSystemAppropriate] = useState("");
  const [systemWhy, setSystemWhy] = useState("");
  const [emotionsBefore, setEmotionsBefore] = useState("");
  const [emotionsWhile, setEmotionsWhile] = useState("");
  const [emotionsAfter, setEmotionsAfter] = useState("");
  const [linkedForecast, setLinkedForecast] = useState("");
  const [extraNotes, setExtraNotes] = useState("");
  const [accordion, setAccordion] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [useNewQuestions, setUseNewQuestions] = useState(true);
  const [showForecastSelect, setShowForecastSelect] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isHoveringShare, setIsHoveringShare] = useState(false);
  const [showDeleteIcon, setShowDeleteIcon] = useState(null);
  const [showGross, setShowGross] = useState(() => localStorage.getItem("show-gross") === "true");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [screenshotToDelete, setScreenshotToDelete] = useState(null);
  const scrollBoxRef = useRef(null);
  const journalQuestionsRef = useRef(null);
  const [hasJumpedToQuestions, setHasJumpedToQuestions] = useState(false);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const handleImageMouseEnter = (index) => {
    setShowDeleteIcon(index);
  };

  const handleImageMouseLeave = (index) => {
    setShowDeleteIcon(null);
  };

  const handleDeleteScreenshot = (fieldName) => {
    setScreenshotToDelete(fieldName);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteScreenshot = () => {
    if (screenshotToDelete) {
      setPairSelected({
        ...pairSelected,
        [screenshotToDelete]: "",
      });
      setScreenshotToDelete(null);
    }
  };

  const scrollToJournalQuestions = () => {
    if (scrollBoxRef.current) {
      setPreviousScrollPosition(scrollBoxRef.current.scrollTop);
    }
    if (journalQuestionsRef.current) {
      journalQuestionsRef.current.scrollIntoView({ behavior: "smooth" });
      setHasJumpedToQuestions(true);
    }
  };

  const scrollToPreviousPosition = () => {
    if (scrollBoxRef.current) {
      scrollBoxRef.current.scrollTo({
        top: previousScrollPosition,
        behavior: "smooth",
      });
      setHasJumpedToQuestions(false);
    }
  };

  const [dxyHourlyBeforeNote, setDxyHourlyBeforeNote] = useState("");
  const [dxyHourlyAfterNote, setDxyHourlyAfterNote] = useState("");
  const [dxyDailyBeforeNote, setDxyDailyBeforeNote] = useState("");
  const [dxyDailyAfterNote, setDxyDailyAfterNote] = useState("");

  const [min5_beforeNote, setMin5_beforeNote] = useState("");
  const [min15_beforeNote, setMin15_beforeNote] = useState("");
  const [min5_afterNote, setMin5_afterNote] = useState("");
  const [min15_afterNote, setMin15_afterNote] = useState("");

  const [oneHour_beforeNote, setOneHour_beforeNote] = useState("");
  const [oneHour_afterNote, setOneHour_afterNote] = useState("");

  const [fourHour_afterNote, setFourHour_afterNote] = useState("");
  const [fourHour_beforeNote, setFourHour_beforeNote] = useState("");

  const [daily_beforeNote, setDaily_beforeNote] = useState("");
  const [daily_afterNote, setDaily_afterNote] = useState("");

  const [weekly_beforeNote, setWeekly_beforeNote] = useState("");

  const [weekly_afterNote, setWeekly_afterNote] = useState("");

  const [formSubmitted, setFormSubmitted] = useState(false);

  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const [isHoveringNext, setIsHoveringNext] = useState(false);
  const [isHoveringBack, setIsHoveringBack] = useState(false);

  const [executionDiscipline, setExecutionDiscipline] = useState(0);
  const [emotionalControl, setEmotionalControl] = useState(0);
  const [setupQuality, setSetupQuality] = useState(0);

  const clearFields = () => {
    setPros("");
    setCons("");
    setEmotions("");
    setTakeAgain("");
    setPlan("");
    setExtraNotes("");
    setMin5_beforeNote("");
    setMin15_beforeNote("");
    setMin5_afterNote("");
    setMin15_afterNote("");
    setOneHour_beforeNote("");
    setOneHour_afterNote("");
    setFourHour_afterNote("");
    setFourHour_beforeNote("");
    setDaily_beforeNote("");
    setDaily_afterNote("");
    setWeekly_beforeNote("");
    setWeekly_afterNote("");
    setDxyHourlyBeforeNote("");
    setDxyHourlyAfterNote("");
    setDxyDailyBeforeNote("");
    setDxyDailyAfterNote("");
    setExecutionDiscipline(0);
    setEmotionalControl(0);
    setSetupQuality(0);
    setExecutionRight("");
    setExecutionDeviate("");
    setSystemAppropriate("");
    setSystemWhy("");
    setEmotionsBefore("");
    setEmotionsWhile("");
    setEmotionsAfter("");
  };

  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  const checkTypeOfpair = (pair) => {
    if (pair.includes("USD")) {
      setUsdPair(true);
    } else {
      setUsdPair(false);
    }
  };

  // Get currency symbol
  const getCurrency = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return getCurrencyForAccount(pairSelected?.accountId, user);
  };

  const fetchFreshTradeData = async (tradeId) => {
    try {
      const response = await DataFetcher("get-trade-by-id", { tradeId });
      if (response) {
        return response;
      }
    } catch (error) {
      console.error("Error fetching fresh trade data:", error);
    }
    return null;
  };

  useEffect(() => {
    setIsLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getJournal(
      JSON.parse(localStorage.getItem("journal-portal")),
      JSON.parse(localStorage.getItem("trade-portal")),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const callback = useCallback(
    (props) => {
      if (props.target.getAttribute("id") === "setLinkedForecast") {
        setLinkedForecast("");
      } else {
        setPairSelected({
          ...pairSelected,
          [props.target.alt]: "",
        });
      }
    },
    [pairSelected],
  );

  const bind = useLongPress(callback, { threshold: 2400 });

  // Calculate dynamic rows based on content
  const calculateRows = (text) => {
    if (!text || text.trim() === "") return 3;
    const lineCount = text.split("\n").length;
    const charCount = text.length;
    // Estimate rows based on characters (roughly 80 chars per row)
    const estimatedRows = Math.ceil(charCount / 80);
    return Math.max(3, Math.max(lineCount, estimatedRows));
  };

  const ScreenShots = (pairSelected) => {
    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            margin: "0 0 16px 0",
          }}
        >
          <div
            onClick={() => {
              setAccordion(accordion ? false : true);
              if (scrollBoxRef.current) {
                scrollBoxRef.current.scrollTop = 0;
              }
            }}
            style={{
              padding: "10px 20px",
              borderRadius: "24px",
              cursor: "pointer",
              backgroundColor:
                theme[0] === "dark"
                  ? "rgba(156, 163, 175, 0.12)"
                  : "rgba(107, 114, 128, 0.08)",
              border: `2px solid ${
                theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
              }`,
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              fontWeight: "600",
              fontSize: "13px",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              transition: "all 0.15s ease",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                theme[0] === "dark"
                  ? "rgba(156, 163, 175, 0.2)"
                  : "rgba(107, 114, 128, 0.15)";
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                theme[0] === "dark"
                  ? "rgba(156, 163, 175, 0.12)"
                  : "rgba(107, 114, 128, 0.08)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {!accordion ? "View All Screenshots" : "Close Screenshots"}
          </div>
        </div>
        <Accordion
          expanded={accordion}
          onChange={() => {}}
          elevation={0}
          sx={{
            backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
            color: theme[0] !== "dark" ? "#374151" : "#e5e7eb",
            borderRadius: "8px",
            "&:before": {
              display: "none",
            },
          }}
        >
          <AccordionSummary
            aria-controls="panel1a-content"
            id="panel1a-header"
            sx={{ display: "none" }}
          ></AccordionSummary>
          <AccordionDetails
            style={{
              display: "flex",
              justifyContent: "space-evenly",
              flexWrap: "wrap",
              padding: "0",
            }}
          >
            <Grid
              container
              spacing={0}
              sx={{ maxWidth: "95%", margin: "0 auto", padding: "0" }}
            >
              <Grid
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
              >
                <div
                  style={{
                    cursor: "pointer",
                    padding: "10px 16px",
                    margin: "0 auto 16px auto",
                    borderRadius: "12px",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    textAlign: "center",
                    maxWidth: "160px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getTitle(0).isEntryTimeframe
                        ? theme[0] === "dark"
                          ? "#ED9E0E"
                          : "#d97706"
                        : theme[0] !== "dark"
                          ? "#1a1b1e"
                          : "#FCFCFF",
                      margin: 0,
                    }}
                  >
                    {getTitle(0).title}
                  </p>
                </div>

                {pairSelected?.min5_before ? (
                  pairSelected?.min5_before.startsWith(
                    "https://www.tradingview.com",
                  ) ? (
                    <div
                      style={{ position: "relative" }}
                      onMouseEnter={() => handleImageMouseEnter(0)}
                      onMouseLeave={() => handleImageMouseLeave(0)}
                    >
                      <img
                        style={imgStyle}
                        src={pairSelected?.min5_before}
                        alt="min5_before"
                        height="auto"
                        onClick={() => openImageViewer(0)}
                      />
                      {showDeleteIcon === 0 && (
                        <Tooltip title="Delete screenshot">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreenshot("min5_before");
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(242, 54, 69, 0.9)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.7)";
                            }}
                          >
                            <DeleteIcon
                              style={{
                                color: "#fff",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#f23645",
                        cursor: "pointer",
                        fontWeight: "300",
                      }}
                      onClick={(e) => {
                        handleDeleteScreenshot("min5_before");
                      }}
                    >
                      Invalid Link
                    </p>
                  )
                ) : (
                  <div
                    style={{
                      minHeight: "60px",
                    }}
                  >
                    <CustomMultiTextField
                      labelColor="#f23645"
                      size="small"
                      rows={1}
                      variant="standard"
                      value={pairSelected?.min5_before}
                      label="No Screenshot Detected"
                      onChange={(e) => {
                        setPairSelected({
                          ...pairSelected,
                          min5_before: e.target.value,
                        });
                      }}
                      fontSize="16px"
                      disableUnderline={true}
                    />
                  </div>
                )}
                <div
                  style={{
                    margin: "1em auto 0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        margin: 0,
                        paddingLeft: "5px",
                      }}
                    >
                      5m Notes
                    </p>
                    <VoiceToText
                      onTranscript={setMin5_beforeNote}
                      currentValue={min5_beforeNote}
                    />
                  </div>
                  <CustomMultiTextField
                    labelColor="white"
                    size="small"
                    rows={min5_beforeNote ? 2 : 0}
                    variant="standard"
                    value={min5_beforeNote}
                    onChange={(e) => {
                      setMin5_beforeNote(e.target.value);
                    }}
                    fontSize="15px"
                    disableUnderline={true}
                  />
                </div>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
              >
                <div
                  style={{
                    cursor: "pointer",
                    padding: "10px 16px",
                    margin: "0 auto 16px auto",
                    borderRadius: "12px",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    textAlign: "center",
                    maxWidth: "160px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getTitle(1).isEntryTimeframe
                        ? theme[0] === "dark"
                          ? "#ED9E0E"
                          : "#d97706"
                        : theme[0] !== "dark"
                          ? "#1a1b1e"
                          : "#FCFCFF",
                      margin: 0,
                    }}
                  >
                    {getTitle(1).title}
                  </p>
                </div>

                {pairSelected?.min5_after ? (
                  pairSelected?.min5_after.startsWith(
                    "https://www.tradingview.com",
                  ) ? (
                    <div
                      style={{ position: "relative" }}
                      onMouseEnter={() => handleImageMouseEnter(1)}
                      onMouseLeave={() => handleImageMouseLeave(1)}
                    >
                      <img
                        style={imgStyle}
                        src={pairSelected?.min5_after}
                        alt="min5_after"
                        height="auto"
                        onClick={() => openImageViewer(1)}
                      />
                      {showDeleteIcon === 1 && (
                        <Tooltip title="Delete screenshot">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreenshot("min5_after");
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(242, 54, 69, 0.9)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.7)";
                            }}
                          >
                            <DeleteIcon
                              style={{
                                color: "#fff",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#f23645",
                        cursor: "pointer",
                        fontWeight: "300",
                      }}
                      onClick={(e) => {
                        handleDeleteScreenshot("min5_after");
                      }}
                    >
                      Invalid Link
                    </p>
                  )
                ) : (
                  <div
                    style={{
                      minHeight: "60px",
                    }}
                  >
                    <CustomMultiTextField
                      labelColor="#f23645"
                      size="small"
                      rows={1}
                      variant="standard"
                      value={pairSelected?.min5_after}
                      label="No Screenshot Detected"
                      onChange={(e) => {
                        setPairSelected({
                          ...pairSelected,
                          min5_after: e.target.value,
                        });
                      }}
                      fontSize="16px"
                      disableUnderline={true}
                    />
                  </div>
                )}
                <div
                  style={{
                    margin: "1em auto 0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        margin: 0,
                        paddingLeft: "5px",
                      }}
                    >
                      5m After Notes
                    </p>
                    <VoiceToText
                      onTranscript={setMin5_afterNote}
                      currentValue={min5_afterNote}
                    />
                  </div>
                  <CustomMultiTextField
                    labelColor="white"
                    size="small"
                    rows={min5_afterNote ? 2 : 0}
                    variant="standard"
                    value={min5_afterNote}
                    onChange={(e) => {
                      setMin5_afterNote(e.target.value);
                    }}
                    fontSize="15px"
                    disableUnderline={true}
                  />
                </div>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
              >
                <div
                  style={{
                    cursor: "pointer",
                    padding: "10px 16px",
                    margin: "0 auto 16px auto",
                    borderRadius: "12px",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    textAlign: "center",
                    maxWidth: "160px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getTitle(2).isEntryTimeframe
                        ? theme[0] === "dark"
                          ? "#ED9E0E"
                          : "#d97706"
                        : theme[0] !== "dark"
                          ? "#1a1b1e"
                          : "#FCFCFF",
                      margin: 0,
                    }}
                  >
                    {getTitle(2).title}
                  </p>
                </div>

                {pairSelected?.min15_before ? (
                  pairSelected?.min15_before.startsWith(
                    "https://www.tradingview.com",
                  ) ? (
                    <div
                      style={{ position: "relative" }}
                      onMouseEnter={() => handleImageMouseEnter(2)}
                      onMouseLeave={() => handleImageMouseLeave(2)}
                    >
                      <img
                        style={imgStyle}
                        src={pairSelected?.min15_before}
                        alt="min15_before"
                        height="auto"
                        onClick={() => openImageViewer(2)}
                      />
                      {showDeleteIcon === 2 && (
                        <Tooltip title="Delete screenshot">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreenshot("min15_before");
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(242, 54, 69, 0.9)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.7)";
                            }}
                          >
                            <DeleteIcon
                              style={{
                                color: "#fff",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#f23645",
                        cursor: "pointer",
                        fontWeight: "300",
                      }}
                      onClick={(e) => {
                        handleDeleteScreenshot("min15_before");
                      }}
                    >
                      Invalid Link
                    </p>
                  )
                ) : (
                  <div
                    style={{
                      minHeight: "60px",
                    }}
                  >
                    <CustomMultiTextField
                      labelColor="#f23645"
                      size="small"
                      rows={1}
                      variant="standard"
                      value={pairSelected?.min15_before}
                      label="No Screenshot Detected"
                      onChange={(e) => {
                        setPairSelected({
                          ...pairSelected,
                          min15_before: e.target.value,
                        });
                      }}
                      fontSize="16px"
                      disableUnderline={true}
                    />
                  </div>
                )}
                <div
                  style={{
                    margin: "1em auto 0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        margin: 0,
                        paddingLeft: "5px",
                      }}
                    >
                      15m Notes
                    </p>
                    <VoiceToText
                      onTranscript={setMin15_beforeNote}
                      currentValue={min15_beforeNote}
                    />
                  </div>
                  <CustomMultiTextField
                    labelColor="white"
                    size="small"
                    rows={min15_beforeNote ? 2 : 0}
                    variant="standard"
                    value={min15_beforeNote}
                    onChange={(e) => {
                      setMin15_beforeNote(e.target.value);
                    }}
                    fontSize="15px"
                    disableUnderline={true}
                  />
                </div>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
              >
                <div
                  style={{
                    cursor: "pointer",
                    padding: "10px 16px",
                    margin: "0 auto 16px auto",
                    borderRadius: "12px",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    textAlign: "center",
                    maxWidth: "160px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getTitle(3).isEntryTimeframe
                        ? theme[0] === "dark"
                          ? "#ED9E0E"
                          : "#d97706"
                        : theme[0] !== "dark"
                          ? "#1a1b1e"
                          : "#FCFCFF",
                      margin: 0,
                    }}
                  >
                    {getTitle(3).title}
                  </p>
                </div>

                {pairSelected?.min15_after ? (
                  pairSelected?.min15_after.startsWith(
                    "https://www.tradingview.com",
                  ) ? (
                    <div
                      style={{ position: "relative" }}
                      onMouseEnter={() => handleImageMouseEnter(3)}
                      onMouseLeave={() => handleImageMouseLeave(3)}
                    >
                      <img
                        style={imgStyle}
                        src={pairSelected?.min15_after}
                        alt="min15_after"
                        height="auto"
                        onClick={() => openImageViewer(3)}
                      />
                      {showDeleteIcon === 3 && (
                        <Tooltip title="Delete screenshot">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreenshot("min15_after");
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(242, 54, 69, 0.9)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.7)";
                            }}
                          >
                            <DeleteIcon
                              style={{
                                color: "#fff",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#f23645",
                        cursor: "pointer",
                        fontWeight: "300",
                      }}
                      onClick={(e) => {
                        handleDeleteScreenshot("min15_after");
                      }}
                    >
                      Invalid Link
                    </p>
                  )
                ) : (
                  <div
                    style={{
                      minHeight: "60px",
                    }}
                  >
                    <CustomMultiTextField
                      labelColor="#f23645"
                      size="small"
                      rows={1}
                      variant="standard"
                      value={pairSelected?.min15_after}
                      label="No Screenshot Detected"
                      onChange={(e) => {
                        setPairSelected({
                          ...pairSelected,
                          min15_after: e.target.value,
                        });
                      }}
                      fontSize="16px"
                      disableUnderline={true}
                    />
                  </div>
                )}
                <div
                  style={{
                    margin: "1em auto 0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        margin: 0,
                        paddingLeft: "5px",
                      }}
                    >
                      15m After Notes
                    </p>
                    <VoiceToText
                      onTranscript={setMin15_afterNote}
                      currentValue={min15_afterNote}
                    />
                  </div>
                  <CustomMultiTextField
                    labelColor="white"
                    size="small"
                    rows={min15_afterNote ? 2 : 0}
                    variant="standard"
                    value={min15_afterNote}
                    onChange={(e) => {
                      setMin15_afterNote(e.target.value);
                    }}
                    fontSize="15px"
                    disableUnderline={true}
                  />
                </div>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
              >
                <div
                  style={{
                    cursor: "pointer",
                    padding: "10px 16px",
                    margin: "0 auto 16px auto",
                    borderRadius: "12px",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    textAlign: "center",
                    maxWidth: "160px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getTitle(4).isEntryTimeframe
                        ? theme[0] === "dark"
                          ? "#ED9E0E"
                          : "#d97706"
                        : theme[0] !== "dark"
                          ? "#1a1b1e"
                          : "#FCFCFF",
                      margin: 0,
                    }}
                  >
                    {getTitle(4).title}
                  </p>
                </div>

                {pairSelected?.hour1_before ? (
                  pairSelected?.hour1_before.startsWith(
                    "https://www.tradingview.com",
                  ) ? (
                    <div
                      style={{ position: "relative" }}
                      onMouseEnter={() => handleImageMouseEnter(4)}
                      onMouseLeave={() => handleImageMouseLeave(4)}
                    >
                      <img
                        style={imgStyle}
                        src={pairSelected?.hour1_before}
                        alt="hour1_before"
                        height="auto"
                        onClick={() => openImageViewer(4)}
                      />
                      {showDeleteIcon === 4 && (
                        <Tooltip title="Delete screenshot">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreenshot("hour1_before");
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(242, 54, 69, 0.9)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.7)";
                            }}
                          >
                            <DeleteIcon
                              style={{
                                color: "#fff",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#f23645",
                        cursor: "pointer",
                        fontWeight: "300",
                      }}
                      onClick={(e) => {
                        handleDeleteScreenshot("hour1_before");
                      }}
                    >
                      Invalid Link
                    </p>
                  )
                ) : (
                  <div
                    style={{
                      minHeight: "60px",
                    }}
                  >
                    <CustomMultiTextField
                      labelColor="#f23645"
                      size="small"
                      rows={1}
                      variant="standard"
                      value={pairSelected?.hour1_before}
                      label="No Screenshot Detected"
                      onChange={(e) => {
                        setPairSelected({
                          ...pairSelected,
                          hour1_before: e.target.value,
                        });
                      }}
                      fontSize="16px"
                      disableUnderline={true}
                    />
                  </div>
                )}
                <div
                  style={{
                    margin: "1em auto 0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        margin: 0,
                        paddingLeft: "5px",
                      }}
                    >
                      1h Before Notes
                    </p>
                    <VoiceToText
                      onTranscript={setOneHour_beforeNote}
                      currentValue={oneHour_beforeNote}
                    />
                  </div>
                  <CustomMultiTextField
                    labelColor="white"
                    size="small"
                    rows={oneHour_beforeNote ? 2 : 0}
                    variant="standard"
                    value={oneHour_beforeNote}
                    onChange={(e) => {
                      setOneHour_beforeNote(e.target.value);
                    }}
                    fontSize="15px"
                    disableUnderline={true}
                  />
                </div>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
              >
                <div
                  style={{
                    cursor: "pointer",
                    padding: "10px 16px",
                    margin: "0 auto 16px auto",
                    borderRadius: "12px",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    textAlign: "center",
                    maxWidth: "160px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getTitle(5).isEntryTimeframe
                        ? theme[0] === "dark"
                          ? "#ED9E0E"
                          : "#d97706"
                        : theme[0] !== "dark"
                          ? "#1a1b1e"
                          : "#FCFCFF",
                      margin: 0,
                    }}
                  >
                    {getTitle(5).title}
                  </p>
                </div>

                {pairSelected?.hour1_after ? (
                  pairSelected?.hour1_after.startsWith(
                    "https://www.tradingview.com",
                  ) ? (
                    <div
                      style={{ position: "relative" }}
                      onMouseEnter={() => handleImageMouseEnter(5)}
                      onMouseLeave={() => handleImageMouseLeave(5)}
                    >
                      <img
                        style={imgStyle}
                        src={pairSelected?.hour1_after}
                        alt="hour1_after"
                        height="auto"
                        onClick={() => openImageViewer(5)}
                      />
                      {showDeleteIcon === 5 && (
                        <Tooltip title="Delete screenshot">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreenshot("hour1_after");
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(242, 54, 69, 0.9)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.7)";
                            }}
                          >
                            <DeleteIcon
                              style={{
                                color: "#fff",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#f23645",
                        cursor: "pointer",
                        fontWeight: "300",
                      }}
                      onClick={(e) => {
                        handleDeleteScreenshot("hour1_after");
                      }}
                    >
                      Invalid Link
                    </p>
                  )
                ) : (
                  <div
                    style={{
                      minHeight: "60px",
                    }}
                  >
                    <CustomMultiTextField
                      labelColor="#f23645"
                      size="small"
                      rows={1}
                      variant="standard"
                      value={pairSelected?.hour1_after}
                      label="No Screenshot Detected"
                      onChange={(e) => {
                        setPairSelected({
                          ...pairSelected,
                          hour1_after: e.target.value,
                        });
                      }}
                      fontSize="16px"
                      disableUnderline={true}
                    />
                  </div>
                )}
                <div
                  style={{
                    margin: "1em auto 0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        margin: 0,
                        paddingLeft: "5px",
                      }}
                    >
                      1h After Notes
                    </p>
                    <VoiceToText
                      onTranscript={setOneHour_afterNote}
                      currentValue={oneHour_afterNote}
                    />
                  </div>
                  <CustomMultiTextField
                    labelColor="white"
                    size="small"
                    rows={oneHour_afterNote ? 2 : 0}
                    variant="standard"
                    value={oneHour_afterNote}
                    onChange={(e) => {
                      setOneHour_afterNote(e.target.value);
                    }}
                    fontSize="15px"
                    disableUnderline={true}
                  />
                </div>
              </Grid>

              {usdPair && (
                <>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ padding: { xs: "0", md: "8px" } }}
                  >
                    <div
                      style={{
                        cursor: "pointer",
                        padding: "10px 16px",
                        margin: "0 auto 16px auto",
                        borderRadius: "12px",
                        border: `2px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                        textAlign: "center",
                        maxWidth: "160px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
                          margin: 0,
                        }}
                      >
                        DXY LTF Before
                      </p>
                    </div>

                    {pairSelected?.dxyHourlyBefore ? (
                      pairSelected?.dxyHourlyBefore.startsWith(
                        "https://www.tradingview.com",
                      ) ? (
                        <div
                          style={{ position: "relative" }}
                          onMouseEnter={() => handleImageMouseEnter(12)}
                          onMouseLeave={() => handleImageMouseLeave(12)}
                        >
                          <img
                            style={imgStyle}
                            src={pairSelected?.dxyHourlyBefore}
                            alt="dxyHourlyBefore"
                            height="auto"
                            onClick={() => openImageViewer(12)}
                          />
                          {showDeleteIcon === 12 && (
                            <Tooltip title="Delete screenshot">
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteScreenshot("dxyHourlyBefore");
                                }}
                                style={{
                                  position: "absolute",
                                  top: "8px",
                                  right: "8px",
                                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                                  borderRadius: "50%",
                                  padding: "8px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "rgba(242, 54, 69, 0.9)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "rgba(0, 0, 0, 0.7)";
                                }}
                              >
                                <DeleteIcon
                                  style={{
                                    color: "#fff",
                                    fontSize: "20px",
                                  }}
                                />
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      ) : (
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "14px",
                            color: "#f23645",
                            cursor: "pointer",
                            fontWeight: "300",
                          }}
                          onClick={(e) => {
                            handleDeleteScreenshot("dxyHourlyBefore");
                          }}
                        >
                          Invalid Link
                        </p>
                      )
                    ) : (
                      <div
                        style={{
                          minHeight: "60px",
                        }}
                      >
                        <CustomMultiTextField
                          labelColor="#f23645"
                          size="small"
                          rows={1}
                          variant="standard"
                          value={pairSelected?.dxyHourlyBefore}
                          label="No Screenshot Detected"
                          onChange={(e) => {
                            setPairSelected({
                              ...pairSelected,
                              dxyHourlyBefore: e.target.value,
                            });
                          }}
                          fontSize="16px"
                          disableUnderline={true}
                        />
                      </div>
                    )}
                    <div
                      style={{
                        minHeight: "69px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "8px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                            margin: 0,
                            paddingLeft: "5px",
                          }}
                        >
                          DXY LTF Before Notes
                        </p>
                        <VoiceToText
                          onTranscript={setDxyHourlyBeforeNote}
                          currentValue={dxyHourlyBeforeNote}
                        />
                      </div>
                      <CustomMultiTextField
                        labelColor="white"
                        size="small"
                        rows={dxyHourlyBeforeNote ? 2 : 0}
                        variant="standard"
                        value={dxyHourlyBeforeNote}
                        onChange={(e) => {
                          setDxyHourlyBeforeNote(e.target.value);
                        }}
                        fontSize="15px"
                        disableUnderline={true}
                      />
                    </div>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ padding: { xs: "0", md: "8px" } }}
                  >
                    <div
                      style={{
                        cursor: "pointer",
                        padding: "10px 16px",
                        margin: "0 auto 16px auto",
                        borderRadius: "12px",
                        border: `2px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                        textAlign: "center",
                        maxWidth: "160px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
                          margin: 0,
                        }}
                      >
                        DXY LTF After
                      </p>
                    </div>

                    {pairSelected?.dxyHourlyAfter ? (
                      pairSelected?.dxyHourlyAfter.startsWith(
                        "https://www.tradingview.com",
                      ) ? (
                        <div
                          style={{ position: "relative" }}
                          onMouseEnter={() => handleImageMouseEnter(13)}
                          onMouseLeave={() => handleImageMouseLeave(13)}
                        >
                          <img
                            style={imgStyle}
                            src={pairSelected?.dxyHourlyAfter}
                            alt="dxyHourlyAfter"
                            height="auto"
                            onClick={() => openImageViewer(13)}
                          />
                          {showDeleteIcon === 13 && (
                            <Tooltip title="Delete screenshot">
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteScreenshot("dxyHourlyAfter");
                                }}
                                style={{
                                  position: "absolute",
                                  top: "8px",
                                  right: "8px",
                                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                                  borderRadius: "50%",
                                  padding: "8px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "rgba(242, 54, 69, 0.9)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "rgba(0, 0, 0, 0.7)";
                                }}
                              >
                                <DeleteIcon
                                  style={{
                                    color: "#fff",
                                    fontSize: "20px",
                                  }}
                                />
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      ) : (
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "14px",
                            color: "#f23645",
                            cursor: "pointer",
                            fontWeight: "300",
                          }}
                          onClick={(e) => {
                            handleDeleteScreenshot("dxyHourlyAfter");
                          }}
                        >
                          Invalid Link
                        </p>
                      )
                    ) : (
                      <div
                        style={{
                          minHeight: "60px",
                        }}
                      >
                        <CustomMultiTextField
                          labelColor="#f23645"
                          size="small"
                          rows={1}
                          variant="standard"
                          value={pairSelected?.dxyHourlyAfter}
                          label="No Screenshot Detected"
                          onChange={(e) => {
                            setPairSelected({
                              ...pairSelected,
                              dxyHourlyAfter: e.target.value,
                            });
                          }}
                          fontSize="16px"
                          disableUnderline={true}
                        />
                      </div>
                    )}
                    <div
                      style={{
                        minHeight: "69px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "8px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                            margin: 0,
                            paddingLeft: "5px",
                          }}
                        >
                          DXY LTF After Notes
                        </p>
                        <VoiceToText
                          onTranscript={setDxyHourlyAfterNote}
                          currentValue={dxyHourlyAfterNote}
                        />
                      </div>
                      <CustomMultiTextField
                        labelColor="white"
                        size="small"
                        rows={dxyHourlyAfterNote ? 2 : 0}
                        variant="standard"
                        value={dxyHourlyAfterNote}
                        onChange={(e) => {
                          setDxyHourlyAfterNote(e.target.value);
                        }}
                        fontSize="15px"
                        disableUnderline={true}
                      />
                    </div>
                  </Grid>
                </>
              )}

              <Grid
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
              >
                <div
                  style={{
                    cursor: "pointer",
                    padding: "10px 16px",
                    margin: "0 auto 16px auto",
                    borderRadius: "12px",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    textAlign: "center",
                    maxWidth: "160px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getTitle(6).isEntryTimeframe
                        ? theme[0] === "dark"
                          ? "#ED9E0E"
                          : "#d97706"
                        : theme[0] !== "dark"
                          ? "#1a1b1e"
                          : "#FCFCFF",
                      margin: 0,
                    }}
                  >
                    {getTitle(6).title}
                  </p>
                </div>

                {pairSelected?.hour4_before ? (
                  pairSelected?.hour4_before.startsWith(
                    "https://www.tradingview.com",
                  ) ? (
                    <div
                      style={{ position: "relative" }}
                      onMouseEnter={() => handleImageMouseEnter(6)}
                      onMouseLeave={() => handleImageMouseLeave(6)}
                    >
                      <img
                        style={imgStyle}
                        src={pairSelected?.hour4_before}
                        alt="hour4_before"
                        height="auto"
                        onClick={() => openImageViewer(6)}
                      />
                      {showDeleteIcon === 6 && (
                        <Tooltip title="Delete screenshot">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreenshot("hour4_before");
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(242, 54, 69, 0.9)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.7)";
                            }}
                          >
                            <DeleteIcon
                              style={{
                                color: "#fff",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#f23645",
                        cursor: "pointer",
                        fontWeight: "300",
                      }}
                      onClick={(e) => {
                        handleDeleteScreenshot("hour4_before");
                      }}
                    >
                      Invalid Link
                    </p>
                  )
                ) : (
                  <div
                    style={{
                      minHeight: "60px",
                    }}
                  >
                    <CustomMultiTextField
                      labelColor="#f23645"
                      size="small"
                      rows={1}
                      variant="standard"
                      value={pairSelected?.hour4_before}
                      label="No Screenshot Detected"
                      onChange={(e) => {
                        setPairSelected({
                          ...pairSelected,
                          hour4_before: e.target.value,
                        });
                      }}
                      fontSize="16px"
                      disableUnderline={true}
                    />
                  </div>
                )}
                <div
                  style={{
                    margin: "1em auto 0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        margin: 0,
                        paddingLeft: "5px",
                      }}
                    >
                      4h After Notes
                    </p>
                    <VoiceToText
                      onTranscript={setFourHour_afterNote}
                      currentValue={fourHour_afterNote}
                    />
                  </div>
                  <CustomMultiTextField
                    labelColor="white"
                    size="small"
                    rows={fourHour_afterNote ? 2 : 0}
                    variant="standard"
                    value={fourHour_afterNote}
                    onChange={(e) => {
                      setFourHour_afterNote(e.target.value);
                    }}
                    fontSize="15px"
                    disableUnderline={true}
                  />
                </div>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
              >
                <div
                  style={{
                    cursor: "pointer",
                    padding: "10px 16px",
                    margin: "0 auto 16px auto",
                    borderRadius: "12px",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    textAlign: "center",
                    maxWidth: "160px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getTitle(7).isEntryTimeframe
                        ? theme[0] === "dark"
                          ? "#ED9E0E"
                          : "#d97706"
                        : theme[0] !== "dark"
                          ? "#1a1b1e"
                          : "#FCFCFF",
                      margin: 0,
                    }}
                  >
                    {getTitle(7).title}
                  </p>
                </div>

                {pairSelected?.hour4_after ? (
                  pairSelected?.hour4_after.startsWith(
                    "https://www.tradingview.com",
                  ) ? (
                    <div
                      style={{ position: "relative" }}
                      onMouseEnter={() => handleImageMouseEnter(7)}
                      onMouseLeave={() => handleImageMouseLeave(7)}
                    >
                      <img
                        style={imgStyle}
                        src={pairSelected?.hour4_after}
                        alt="hour4_after"
                        height="auto"
                        onClick={() => openImageViewer(7)}
                      />
                      {showDeleteIcon === 7 && (
                        <Tooltip title="Delete screenshot">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreenshot("hour4_after");
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(242, 54, 69, 0.9)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.7)";
                            }}
                          >
                            <DeleteIcon
                              style={{
                                color: "#fff",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#f23645",
                        cursor: "pointer",
                        fontWeight: "300",
                      }}
                      onClick={(e) => {
                        handleDeleteScreenshot("hour4_after");
                      }}
                    >
                      Invalid Link
                    </p>
                  )
                ) : (
                  <div
                    style={{
                      minHeight: "60px",
                    }}
                  >
                    <CustomMultiTextField
                      labelColor="#f23645"
                      size="small"
                      rows={1}
                      variant="standard"
                      value={pairSelected?.hour4_after}
                      label="No Screenshot Detected"
                      onChange={(e) => {
                        setPairSelected({
                          ...pairSelected,
                          hour4_after: e.target.value,
                        });
                      }}
                      fontSize="16px"
                      disableUnderline={true}
                    />
                  </div>
                )}
                <div
                  style={{
                    margin: "1em auto 0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        margin: 0,
                        paddingLeft: "5px",
                      }}
                    >
                      4h Before Notes
                    </p>
                    <VoiceToText
                      onTranscript={setFourHour_beforeNote}
                      currentValue={fourHour_beforeNote}
                    />
                  </div>
                  <CustomMultiTextField
                    labelColor="white"
                    size="small"
                    rows={fourHour_beforeNote ? 2 : 0}
                    variant="standard"
                    value={fourHour_beforeNote}
                    onChange={(e) => {
                      setFourHour_beforeNote(e.target.value);
                    }}
                    fontSize="15px"
                    disableUnderline={true}
                  />
                </div>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
              >
                <div
                  style={{
                    cursor: "pointer",
                    padding: "10px 16px",
                    margin: "0 auto 16px auto",
                    borderRadius: "12px",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    textAlign: "center",
                    maxWidth: "160px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getTitle(8).isEntryTimeframe
                        ? theme[0] === "dark"
                          ? "#ED9E0E"
                          : "#d97706"
                        : theme[0] !== "dark"
                          ? "#1a1b1e"
                          : "#FCFCFF",
                      margin: 0,
                    }}
                  >
                    {getTitle(8).title}
                  </p>
                </div>

                {pairSelected?.daily_before ? (
                  pairSelected?.daily_before.startsWith(
                    "https://www.tradingview.com",
                  ) ? (
                    <div
                      style={{ position: "relative" }}
                      onMouseEnter={() => handleImageMouseEnter(8)}
                      onMouseLeave={() => handleImageMouseLeave(8)}
                    >
                      <img
                        style={imgStyle}
                        src={pairSelected?.daily_before}
                        alt="daily_before"
                        height="auto"
                        onClick={() => openImageViewer(8)}
                      />
                      {showDeleteIcon === 8 && (
                        <Tooltip title="Delete screenshot">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreenshot("daily_before");
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(242, 54, 69, 0.9)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.7)";
                            }}
                          >
                            <DeleteIcon
                              style={{
                                color: "#fff",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#f23645",
                        cursor: "pointer",
                        fontWeight: "300",
                      }}
                      onClick={(e) => {
                        handleDeleteScreenshot("daily_before");
                      }}
                    >
                      Invalid Link
                    </p>
                  )
                ) : (
                  <div
                    style={{
                      minHeight: "60px",
                    }}
                  >
                    <CustomMultiTextField
                      labelColor="#f23645"
                      size="small"
                      rows={1}
                      variant="standard"
                      value={pairSelected?.daily_before}
                      label="No Screenshot Detected"
                      onChange={(e) => {
                        setPairSelected({
                          ...pairSelected,
                          daily_before: e.target.value,
                        });
                      }}
                      fontSize="16px"
                      disableUnderline={true}
                    />
                  </div>
                )}
                <div
                  style={{
                    margin: "1em auto 0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        margin: 0,
                        paddingLeft: "5px",
                      }}
                    >
                      Daily Before Notes
                    </p>
                    <VoiceToText
                      onTranscript={setDaily_beforeNote}
                      currentValue={daily_beforeNote}
                    />
                  </div>
                  <CustomMultiTextField
                    labelColor="white"
                    size="small"
                    rows={daily_beforeNote ? 2 : 0}
                    variant="standard"
                    value={daily_beforeNote}
                    onChange={(e) => {
                      setDaily_beforeNote(e.target.value);
                    }}
                    fontSize="15px"
                    disableUnderline={true}
                  />
                </div>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
              >
                <div
                  style={{
                    cursor: "pointer",
                    padding: "10px 16px",
                    margin: "0 auto 16px auto",
                    borderRadius: "12px",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    textAlign: "center",
                    maxWidth: "160px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getTitle(9).isEntryTimeframe
                        ? theme[0] === "dark"
                          ? "#ED9E0E"
                          : "#d97706"
                        : theme[0] !== "dark"
                          ? "#1a1b1e"
                          : "#FCFCFF",
                      margin: 0,
                    }}
                  >
                    {getTitle(9).title}
                  </p>
                </div>

                {pairSelected?.daily_after ? (
                  pairSelected?.daily_after.startsWith(
                    "https://www.tradingview.com",
                  ) ? (
                    <div
                      style={{ position: "relative" }}
                      onMouseEnter={() => handleImageMouseEnter(9)}
                      onMouseLeave={() => handleImageMouseLeave(9)}
                    >
                      <img
                        style={imgStyle}
                        src={pairSelected?.daily_after}
                        alt="daily_after"
                        height="auto"
                        onClick={() => openImageViewer(9)}
                      />
                      {showDeleteIcon === 9 && (
                        <Tooltip title="Delete screenshot">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreenshot("daily_after");
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(242, 54, 69, 0.9)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.7)";
                            }}
                          >
                            <DeleteIcon
                              style={{
                                color: "#fff",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#f23645",
                        cursor: "pointer",
                        fontWeight: "300",
                      }}
                      onClick={(e) => {
                        handleDeleteScreenshot("daily_after");
                      }}
                    >
                      Invalid Link
                    </p>
                  )
                ) : (
                  <div
                    style={{
                      minHeight: "60px",
                    }}
                  >
                    <CustomMultiTextField
                      labelColor="#f23645"
                      size="small"
                      rows={1}
                      variant="standard"
                      value={pairSelected?.daily_after}
                      label="No Screenshot Detected"
                      onChange={(e) => {
                        setPairSelected({
                          ...pairSelected,
                          daily_after: e.target.value,
                        });
                      }}
                      fontSize="16px"
                      disableUnderline={true}
                    />
                  </div>
                )}
                <div
                  style={{
                    margin: "1em auto 0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        margin: 0,
                        paddingLeft: "5px",
                      }}
                    >
                      Daily After Notes
                    </p>
                    <VoiceToText
                      onTranscript={setDaily_afterNote}
                      currentValue={daily_afterNote}
                    />
                  </div>
                  <CustomMultiTextField
                    labelColor="white"
                    size="small"
                    rows={daily_afterNote ? 2 : 0}
                    variant="standard"
                    value={daily_afterNote}
                    onChange={(e) => {
                      setDaily_afterNote(e.target.value);
                    }}
                    fontSize="15px"
                    disableUnderline={true}
                  />
                </div>
              </Grid>
              {usdPair && (
                <>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ padding: { xs: "0", md: "8px" } }}
                  >
                    <div
                      style={{
                        cursor: "pointer",
                        padding: "10px 16px",
                        margin: "0 auto 16px auto",
                        borderRadius: "12px",
                        border: `2px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                        textAlign: "center",
                        maxWidth: "160px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
                          margin: 0,
                        }}
                      >
                        DXY HTF Before
                      </p>
                    </div>

                    {pairSelected?.dxyDailyBefore ? (
                      pairSelected?.dxyDailyBefore.startsWith(
                        "https://www.tradingview.com",
                      ) ? (
                        <div
                          style={{ position: "relative" }}
                          onMouseEnter={() => handleImageMouseEnter(14)}
                          onMouseLeave={() => handleImageMouseLeave(14)}
                        >
                          <img
                            style={imgStyle}
                            src={pairSelected?.dxyDailyBefore}
                            alt="dxyDailyBefore"
                            height="auto"
                            onClick={() => openImageViewer(14)}
                          />
                          {showDeleteIcon === 14 && (
                            <Tooltip title="Delete screenshot">
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteScreenshot("dxyDailyBefore");
                                }}
                                style={{
                                  position: "absolute",
                                  top: "8px",
                                  right: "8px",
                                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                                  borderRadius: "50%",
                                  padding: "8px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "rgba(242, 54, 69, 0.9)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "rgba(0, 0, 0, 0.7)";
                                }}
                              >
                                <DeleteIcon
                                  style={{
                                    color: "#fff",
                                    fontSize: "20px",
                                  }}
                                />
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      ) : (
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "14px",
                            color: "#f23645",
                            cursor: "pointer",
                            fontWeight: "300",
                          }}
                          onClick={(e) => {
                            handleDeleteScreenshot("dxyDailyBefore");
                          }}
                        >
                          Invalid Link
                        </p>
                      )
                    ) : (
                      <div
                        style={{
                          minHeight: "60px",
                        }}
                      >
                        <CustomMultiTextField
                          labelColor="#f23645"
                          size="small"
                          rows={1}
                          variant="standard"
                          value={pairSelected?.dxyDailyBefore}
                          label="No Screenshot Detected"
                          onChange={(e) => {
                            setPairSelected({
                              ...pairSelected,
                              dxyDailyBefore: e.target.value,
                            });
                          }}
                          fontSize="16px"
                          disableUnderline={true}
                        />
                      </div>
                    )}
                    <div
                      style={{
                        minHeight: "69px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "8px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                            margin: 0,
                            paddingLeft: "5px",
                          }}
                        >
                          DXY HTF Before Notes
                        </p>
                        <VoiceToText
                          onTranscript={setDxyDailyBeforeNote}
                          currentValue={dxyDailyBeforeNote}
                        />
                      </div>
                      <CustomMultiTextField
                        labelColor="white"
                        size="small"
                        rows={dxyDailyBeforeNote ? 2 : 0}
                        variant="standard"
                        value={dxyDailyBeforeNote}
                        onChange={(e) => {
                          setDxyDailyBeforeNote(e.target.value);
                        }}
                        fontSize="15px"
                        disableUnderline={true}
                      />
                    </div>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ padding: { xs: "0", md: "8px" } }}
                  >
                    <div
                      style={{
                        cursor: "pointer",
                        padding: "10px 16px",
                        margin: "0 auto 16px auto",
                        borderRadius: "12px",
                        border: `2px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                        textAlign: "center",
                        maxWidth: "160px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
                          margin: 0,
                        }}
                      >
                        DXY HTF After
                      </p>
                    </div>

                    {pairSelected?.dxyDailyAfter ? (
                      pairSelected?.dxyDailyAfter.startsWith(
                        "https://www.tradingview.com",
                      ) ? (
                        <div
                          style={{ position: "relative" }}
                          onMouseEnter={() => handleImageMouseEnter(15)}
                          onMouseLeave={() => handleImageMouseLeave(15)}
                        >
                          <img
                            style={imgStyle}
                            src={pairSelected?.dxyDailyAfter}
                            alt="dxyDailyAfter"
                            height="auto"
                            onClick={() => openImageViewer(15)}
                          />
                          {showDeleteIcon === 15 && (
                            <Tooltip title="Delete screenshot">
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteScreenshot("dxyDailyAfter");
                                }}
                                style={{
                                  position: "absolute",
                                  top: "8px",
                                  right: "8px",
                                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                                  borderRadius: "50%",
                                  padding: "8px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "rgba(242, 54, 69, 0.9)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "rgba(0, 0, 0, 0.7)";
                                }}
                              >
                                <DeleteIcon
                                  style={{
                                    color: "#fff",
                                    fontSize: "20px",
                                  }}
                                />
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      ) : (
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "14px",
                            color: "#f23645",
                            cursor: "pointer",
                            fontWeight: "300",
                          }}
                          onClick={(e) => {
                            handleDeleteScreenshot("dxyDailyAfter");
                          }}
                        >
                          Invalid Link
                        </p>
                      )
                    ) : (
                      <div
                        style={{
                          minHeight: "60px",
                        }}
                      >
                        <CustomMultiTextField
                          labelColor="#f23645"
                          size="small"
                          rows={1}
                          variant="standard"
                          value={pairSelected?.dxyDailyAfter}
                          label="No Screenshot Detected"
                          onChange={(e) => {
                            setPairSelected({
                              ...pairSelected,
                              dxyDailyAfter: e.target.value,
                            });
                          }}
                          fontSize="16px"
                          disableUnderline={true}
                        />
                      </div>
                    )}
                    <div
                      style={{
                        minHeight: "69px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "8px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                            margin: 0,
                            paddingLeft: "5px",
                          }}
                        >
                          DXY HTF After Notes
                        </p>
                        <VoiceToText
                          onTranscript={setDxyDailyAfterNote}
                          currentValue={dxyDailyAfterNote}
                        />
                      </div>
                      <CustomMultiTextField
                        labelColor="white"
                        size="small"
                        rows={dxyDailyAfterNote ? 2 : 0}
                        variant="standard"
                        value={dxyDailyAfterNote}
                        onChange={(e) => {
                          setDxyDailyAfterNote(e.target.value);
                        }}
                        fontSize="15px"
                        disableUnderline={true}
                      />
                    </div>
                  </Grid>
                </>
              )}

              <Grid
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
              >
                <div
                  style={{
                    cursor: "pointer",
                    padding: "10px 16px",
                    margin: "0 auto 16px auto",
                    borderRadius: "12px",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    textAlign: "center",
                    maxWidth: "160px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getTitle(10).isEntryTimeframe
                        ? theme[0] === "dark"
                          ? "#ED9E0E"
                          : "#d97706"
                        : theme[0] !== "dark"
                          ? "#1a1b1e"
                          : "#FCFCFF",
                      margin: 0,
                    }}
                  >
                    {getTitle(10).title}
                  </p>
                </div>

                {pairSelected?.weekly_before ? (
                  pairSelected?.weekly_before.startsWith(
                    "https://www.tradingview.com",
                  ) ? (
                    <div
                      style={{ position: "relative" }}
                      onMouseEnter={() => handleImageMouseEnter(10)}
                      onMouseLeave={() => handleImageMouseLeave(10)}
                    >
                      <img
                        style={imgStyle}
                        src={pairSelected?.weekly_before}
                        alt="weekly_before"
                        height="auto"
                        onClick={() => openImageViewer(10)}
                      />
                      {showDeleteIcon === 10 && (
                        <Tooltip title="Delete screenshot">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreenshot("weekly_before");
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(242, 54, 69, 0.9)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.7)";
                            }}
                          >
                            <DeleteIcon
                              style={{
                                color: "#fff",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#f23645",
                        cursor: "pointer",
                        fontWeight: "300",
                      }}
                      onClick={(e) => {
                        handleDeleteScreenshot("weekly_before");
                      }}
                    >
                      Invalid Link
                    </p>
                  )
                ) : (
                  <div
                    style={{
                      minHeight: "60px",
                    }}
                  >
                    <CustomMultiTextField
                      labelColor="#f23645"
                      size="small"
                      rows={1}
                      variant="standard"
                      value={pairSelected?.weekly_before}
                      label="No Screenshot Detected"
                      onChange={(e) => {
                        setPairSelected({
                          ...pairSelected,
                          weekly_before: e.target.value,
                        });
                      }}
                      fontSize="16px"
                      disableUnderline={true}
                    />
                  </div>
                )}
                <div
                  style={{
                    margin: "1em auto 0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        margin: 0,
                        paddingLeft: "5px",
                      }}
                    >
                      Weekly Before Notes
                    </p>
                    <VoiceToText
                      onTranscript={setWeekly_beforeNote}
                      currentValue={weekly_beforeNote}
                    />
                  </div>
                  <CustomMultiTextField
                    labelColor="white"
                    size="small"
                    rows={weekly_beforeNote ? 2 : 0}
                    variant="standard"
                    value={weekly_beforeNote}
                    onChange={(e) => {
                      setWeekly_beforeNote(e.target.value);
                    }}
                    fontSize="15px"
                    disableUnderline={true}
                  />
                </div>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                sx={{ padding: { xs: "0", md: "8px" } }}
              >
                <div
                  style={{
                    cursor: "pointer",
                    padding: "10px 16px",
                    margin: "0 auto 16px auto",
                    borderRadius: "12px",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    textAlign: "center",
                    maxWidth: "160px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getTitle(11).isEntryTimeframe
                        ? theme[0] === "dark"
                          ? "#ED9E0E"
                          : "#d97706"
                        : theme[0] !== "dark"
                          ? "#1a1b1e"
                          : "#FCFCFF",
                      margin: 0,
                    }}
                  >
                    {getTitle(11).title}
                  </p>
                </div>

                {pairSelected?.weekly_after ? (
                  pairSelected?.weekly_after.startsWith(
                    "https://www.tradingview.com",
                  ) ? (
                    <div
                      style={{ position: "relative" }}
                      onMouseEnter={() => handleImageMouseEnter(11)}
                      onMouseLeave={() => handleImageMouseLeave(11)}
                    >
                      <img
                        style={imgStyle}
                        src={pairSelected?.weekly_after}
                        alt="weekly_after"
                        height="auto"
                        onClick={() => openImageViewer(11)}
                      />
                      {showDeleteIcon === 11 && (
                        <Tooltip title="Delete screenshot">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScreenshot("weekly_after");
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(242, 54, 69, 0.9)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.7)";
                            }}
                          >
                            <DeleteIcon
                              style={{
                                color: "#fff",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "14px",
                        color: "#f23645",
                        cursor: "pointer",
                        fontWeight: "300",
                      }}
                      onClick={(e) => {
                        handleDeleteScreenshot("weekly_after");
                      }}
                    >
                      Invalid Link
                    </p>
                  )
                ) : (
                  <div
                    style={{
                      minHeight: "60px",
                    }}
                  >
                    <CustomMultiTextField
                      labelColor="#f23645"
                      size="small"
                      rows={1}
                      variant="standard"
                      value={pairSelected?.weekly_after}
                      label="No Screenshot Detected"
                      onChange={(e) => {
                        setPairSelected({
                          ...pairSelected,
                          weekly_after: e.target.value,
                        });
                      }}
                      fontSize="16px"
                      disableUnderline={true}
                    />
                  </div>
                )}
                <div
                  style={{
                    margin: "1em auto 0 auto",
                    padding: "16px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "8px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        margin: 0,
                        paddingLeft: "5px",
                      }}
                    >
                      Weekly After Notes
                    </p>
                    <VoiceToText
                      onTranscript={setWeekly_afterNote}
                      currentValue={weekly_afterNote}
                    />
                  </div>
                  <CustomMultiTextField
                    labelColor="white"
                    size="small"
                    rows={weekly_afterNote ? 2 : 0}
                    variant="standard"
                    value={weekly_afterNote}
                    onChange={(e) => {
                      setWeekly_afterNote(e.target.value);
                    }}
                    fontSize="15px"
                    disableUnderline={true}
                  />
                </div>
              </Grid>
            </Grid>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                marginTop: "20px",
                paddingBottom: "10px",
              }}
            >
              <div
                onClick={() => {
                  setAccordion(false);
                  if (scrollBoxRef.current) {
                    scrollBoxRef.current.scrollTop = 0;
                  }
                }}
                style={{
                  padding: "10px 20px",
                  borderRadius: "24px",
                  cursor: "pointer",
                  backgroundColor:
                    theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.12)"
                      : "rgba(107, 114, 128, 0.08)",
                  border: `2px solid ${
                    theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                  }`,
                  color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                  fontWeight: "600",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  transition: "all 0.15s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.2)"
                      : "rgba(107, 114, 128, 0.15)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.12)"
                      : "rgba(107, 114, 128, 0.08)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                Close Screenshots
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      </>
    );
  };
  const goBack = () => {
    onClose();
    clearFields();
    setAccordion(false);
    localStorage.removeItem("trade-portal");
    localStorage.removeItem("journal-portal");
    localStorage.removeItem("reflection-trade-ids");
  };
  const getJournal = async (journal, trade) => {
    if (
      journal !== undefined &&
      journal !== null &&
      trade !== undefined &&
      trade !== null
    ) {
      // Fetch fresh trade data from server to get updated netDollar values
      const freshData = await fetchFreshTradeData(trade._id);

      // Merge fresh data with localStorage trade data, prioritizing fresh data
      const mergedTrade = freshData ? { ...trade, ...freshData } : trade;

      // Find journal - try multiple strategies
      let found;
      const journal_trade__id_to_find = mergedTrade._id;

      // Strategy 1: Find by trade_id (most reliable for original trades)
      found = auth.journals?.find(
        (j) => j?.trade_id === journal_trade__id_to_find,
      );

      if (!found) {
        found = auth.journalsBacktesting?.find(
          (j) => j?.trade_id === journal_trade__id_to_find,
        );
      }

      // Strategy 2: If not found and trade has journalID, try finding by journalID
      if (!found && mergedTrade.journalID) {
        found = auth.journals?.find(
          (j) => j?.journalID === mergedTrade.journalID,
        );

        if (!found) {
          found = auth.journalsBacktesting?.find(
            (j) => j?.journalID === mergedTrade.journalID,
          );
        }
      }

      checkTypeOfpair(mergedTrade.pair);
      setPairSelected(mergedTrade);

      const journalDate = new Date(found?.date || mergedTrade.created);
      const shouldUseNewQuestions = journalDate > QUESTION_CUTOFF_DATE;
      setUseNewQuestions(shouldUseNewQuestions);

      if (found) {
        setPros(found.pros);
        setCons(found.cons);
        setEmotions(found.emotions);
        setPlan(found.plan);
        setTakeAgain(found.takeAgain);
        setExtraNotes(found.extraNotes);
        setLinkedForecast(found.foreCastId);

        setDxyHourlyBeforeNote(found.dxyHourlyBeforeNote);
        setDxyHourlyAfterNote(found.dxyHourlyAfterNote);
        setDxyDailyBeforeNote(found.dxyDailyBeforeNote);
        setDxyDailyAfterNote(found.dxyDailyAfterNote);
        setMin5_beforeNote(found.min5_beforeNote);
        setMin15_beforeNote(found.min15_beforeNote);
        setMin5_afterNote(found.min5_afterNote);
        setMin15_afterNote(found.min15_afterNote);
        setOneHour_beforeNote(found.oneHour_beforeNote);
        setOneHour_afterNote(found.oneHour_afterNote);
        setFourHour_afterNote(found.fourHour_afterNote);
        setFourHour_beforeNote(found.fourHour_beforeNote);
        setDaily_beforeNote(found.daily_beforeNote);
        setDaily_afterNote(found.daily_afterNote);
        setWeekly_beforeNote(found.weekly_beforeNote);
        setWeekly_afterNote(found.weekly_afterNote);
        setExecutionDiscipline(mergedTrade.executionDiscipline || 0);
        setEmotionalControl(mergedTrade.emotionalControl || 0);
        setSetupQuality(mergedTrade.setupQuality || 0);
        setExecutionRight(found.executionRight || "");
        setExecutionDeviate(found.executionDeviate || "");
        setSystemAppropriate(found.systemAppropriate || "");
        setSystemWhy(found.systemWhy || "");
        setEmotionsBefore(found.emotionsBefore || "");
        setEmotionsWhile(found.emotionsWhile || "");
        setEmotionsAfter(found.emotionsAfter || "");
      }
      setIsLoading(false);
    }
  };
  const editJournal = async (editedJournal) => {
    if (!formSubmitted);
    setFormSubmitted(true);

    const links = {
      dxyHourlyBefore: pairSelected.dxyHourlyBefore,
      dxyHourlyAfter: pairSelected.dxyHourlyAfter,
      dxyDailyBefore: pairSelected.dxyDailyBefore,
      dxyDailyAfter: pairSelected.dxyDailyAfter,

      min5_before: pairSelected.min5_before,
      min15_before: pairSelected.min15_before,
      min5_after: pairSelected.min5_after,
      min15_after: pairSelected.min15_after,
      hour1_before: pairSelected.hour1_before,
      hour1_after: pairSelected.hour1_after,
      hour4_before: pairSelected.hour4_before,
      hour4_after: pairSelected.hour4_after,
      daily_before: pairSelected.daily_before,
      daily_after: pairSelected.daily_after,
      weekly_before: pairSelected.weekly_before,
      weekly_after: pairSelected.weekly_after,
    };

    let schema;
    schema = {
      backtestingMode: auth.backtestingMode,
      trade_id: editedJournal.trade_id,
      journalID: editedJournal.journalID,
      trade_payload: Object.assign(editedJournal.tradeToUpdate, {
        links: links,
        executionDiscipline: editedJournal.executionDiscipline,
        emotionalControl: editedJournal.emotionalControl,
        setupQuality: editedJournal.setupQuality,
      }),
      journal_payload: {
        cons: editedJournal.cons,
        pros: editedJournal.pros,
        emotions: editedJournal.emotions,
        extraNotes: editedJournal.extraNotes,
        plan: editedJournal.plan,
        takeAgain: editedJournal.takeAgain,
        foreCastId: linkedForecast,
        dxyHourlyBeforeNote: editedJournal.dxyHourlyBeforeNote,
        dxyHourlyAfterNote: editedJournal.dxyHourlyAfterNote,
        dxyDailyBeforeNote: editedJournal.dxyDailyBeforeNote,
        dxyDailyAfterNote: editedJournal.dxyDailyAfterNote,
        min5_beforeNote: editedJournal.min5_beforeNote,
        min15_beforeNote: editedJournal.min15_beforeNote,
        min5_afterNote: editedJournal.min5_afterNote,
        min15_afterNote: editedJournal.min15_afterNote,
        oneHour_beforeNote: editedJournal.oneHour_beforeNote,
        oneHour_afterNote: editedJournal.oneHour_afterNote,
        fourHour_afterNote: editedJournal.fourHour_afterNote,
        fourHour_beforeNote: editedJournal.fourHour_beforeNote,
        daily_beforeNote: editedJournal.daily_beforeNote,
        daily_afterNote: editedJournal.daily_afterNote,
        weekly_beforeNote: editedJournal.weekly_beforeNote,
        weekly_afterNote: editedJournal.weeklyAfterNote,
        executionRight: editedJournal.executionRight,
        executionDeviate: editedJournal.executionDeviate,
        systemAppropriate: editedJournal.systemAppropriate,
        systemWhy: editedJournal.systemWhy,
        emotionsBefore: editedJournal.emotionsBefore,
        emotionsWhile: editedJournal.emotionsWhile,
        emotionsAfter: editedJournal.emotionsAfter,
      },
    };

    const result = await DataFetcher("edit-journal", schema);

    if (result) {
      const updated_trade = result.trade;
      const updated_journal = result.journal;

      const filteredArrayTrades = (auth.trades || []).filter(
        (x) => x && x._id !== updated_trade._id,
      );
      filteredArrayTrades.push(updated_trade);
      auth.setTrades(filteredArrayTrades);

      if (auth.backtestingMode) {
        if (calculateFields !== false) {
          calculateFields(
            filteredArrayTrades,
            true,
            localStorage.getItem("backtest-active"),
          );
        }
      } else {
        if (calculateFields !== false) {
          calculateFields(filteredArrayTrades, false);
        }
      }

      let filteredArray;
      if (auth.backtestingMode) {
        filteredArray = (auth.journalsBacktesting || []).filter(
          (x) => x && x._id !== updated_journal._id,
        );
        filteredArray.push(updated_journal);
        auth.setJournalsBacktesting(filteredArray);
        throwMessage("success", "Success");
        setFormSubmitted(false);
      } else {
        filteredArray = (auth.journals || []).filter(
          (x) => x && x._id !== updated_journal._id,
        );
        filteredArray.push(updated_journal);
        auth.setJournals(filteredArray);
        throwMessage("success", "Success");
        setFormSubmitted(false);
      }
    } else {
      throwMessage("error", "Something went wrong");
      setFormSubmitted(false);
    }
  };

  const handleShare = async () => {
    if (isSharing) return;

    setIsSharing(true);

    try {
      // Use the current journal entry from pairSelected state instead of localStorage
      if (!pairSelected || !pairSelected.journalID) {
        throwMessage("error", "Journal data not found");
        return;
      }

      const schema = {
        journalID: pairSelected.journalID,
      };

      const result = await DataFetcher("generate-journal-share-token", schema);

      if (result && result.slug) {
        const directionLower = result.direction
          ? result.direction.toLowerCase()
          : "unknown";
        const pairLower = result.pair ? result.pair.toLowerCase() : "unknown";
        const dateSlug = result.slug.includes("/")
          ? result.slug.split("/")[1]
          : result.slug;
        const shareUrl = `${window.location.origin}/journal/${pairLower}/${directionLower}/${dateSlug}`;
        await navigator.clipboard.writeText(shareUrl);
        throwMessage("success", "Copied to clipboard");
      } else {
        throwMessage("error", "Failed to generate share link");
      }
    } catch (error) {
      throwMessage("error", "Error sharing journal");
    } finally {
      setIsSharing(false);
    }
  };

  const noForecastToLinkItem = [
    {
      value: "",
      label: "No Forecast Linked",
    },
  ];

  let forecastsMenuItem = auth.forecasts

    .map((item) => {
      return {
        value: item._id,
        label: `${moment(item.date).format("MMMM Do YYYY")} /  ${item.pair} - ${
          item.direction
        }`,
      };
    })
    .reverse()
    .slice(0, 50);

  let forecastsBacktestingMenuItem = [
    ...auth.forecasts.map((item) => {
      return {
        value: item._id,
        label: `${moment(item.date).format("MMMM Do YYYY")} /  ${
          item.pair
        } - ${item.direction}`,
      };
    }),
    ...auth.forecastsBacktesting.map((item) => {
      return {
        value: item._id,
        label: `${moment(item.date).format("MMMM Do YYYY")} /  ${
          item.pair
        } - ${item.direction}`,
      };
    }),
  ]
    .reverse()
    .slice(0, 50);

  forecastsBacktestingMenuItem = JSON.stringify([
    ...noForecastToLinkItem,
    ...forecastsBacktestingMenuItem,
  ]);

  forecastsMenuItem = JSON.stringify([
    ...noForecastToLinkItem,
    ...forecastsMenuItem,
  ]);

  const urls = [
    pairSelected?.min5_before,
    pairSelected?.min5_after,
    pairSelected?.min15_before,
    pairSelected?.min15_after,
    pairSelected?.hour1_before,
    pairSelected?.hour1_after,
    pairSelected?.hour4_before,
    pairSelected?.hour4_after,
    pairSelected?.daily_before,
    pairSelected?.daily_after,
    pairSelected?.weekly_before,
    pairSelected?.weekly_after,
    pairSelected?.dxyHourlyBefore,
    pairSelected?.dxyHourlyAfter,
    pairSelected?.dxyDailyBefore,
    pairSelected?.dxyDailyAfter,
  ];

  const getTitle = (index) => {
    let title;
    switch (index) {
      case 0:
        title = "5m Before";
        break;
      case 1:
        title = "5m After";
        break;
      case 2:
        title = "15m Before";
        break;
      case 3:
        title = "15m After";
        break;
      case 4:
        title = "H1 Before";
        break;
      case 5:
        title = "H1 After";
        break;
      case 6:
        title = "H4 Before";
        break;
      case 7:
        title = "H4 After";
        break;
      case 8:
        title = "Daily Before";
        break;
      case 9:
        title = "Daily After";
        break;
      case 10:
        title = "Weekly Before";
        break;
      case 11:
        title = "Weekly After";
        break;
      case 12:
        title = "1h DXY Before";
        break;
      case 13:
        title = "1h DXY After";
        break;
      case 14:
        title = "D DXY Before";
        break;
      case 15:
        title = "D DXY After";
        break;
      default:
        break;
    }

    // Check if this timeframe matches the entry timeframe
    let isEntryTimeframe = false;
    if (pairSelected && pairSelected.entryTimeframe) {
      const entryTF = pairSelected.entryTimeframe;
      switch (index) {
        case 0:
        case 1:
          isEntryTimeframe = entryTF === "5m";
          break;
        case 2:
        case 3:
          isEntryTimeframe = entryTF === "15m";
          break;
        case 4:
        case 5:
          isEntryTimeframe = entryTF === "1H";
          break;
        case 6:
        case 7:
          isEntryTimeframe = entryTF === "4H";
          break;
        case 8:
        case 9:
          isEntryTimeframe = entryTF === "D";
          break;
        case 10:
        case 11:
          isEntryTimeframe = entryTF === "W";
          break;
        default:
          isEntryTimeframe = false;
      }
    }

    return { title, isEntryTimeframe };
  };

  useEffect(() => {
    document.onkeydown = checkKey;
    function checkKey(e) {
      if (e.keyCode === 39) {
        closeImageViewer(0);
      }
      if (e.keyCode === 37) {
        closeImageViewer(0);
      }
      if (e.keyCode === 76) {
        closeImageViewer(0);
      }
      if (e.keyCode === 72) {
        closeImageViewer(0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for gross-net-changed events from dashboard
  useEffect(() => {
    const handleGrossNetChanged = (event) => {
      if (event.detail && event.detail.showGross !== undefined) {
        setShowGross(event.detail.showGross);
      }
    };

    window.addEventListener("gross-net-changed", handleGrossNetChanged);

    return () => {
      window.removeEventListener("gross-net-changed", handleGrossNetChanged);
    };
  }, []);

  const changeJournal = async (direction, journals) => {
    let next;
    let back;
    let array;

    const testId = localStorage.getItem("backtest-active");
    const reflectionToView = localStorage.getItem("reflection-to-view");
    const missedTradeFilter = localStorage.getItem("missed-trade");

    // In backtesting mode, start with journalsBacktesting and filter by testId/missed/reflection
    if (auth.backtestingMode) {
      // If opened from reflection, use both journals arrays since reflections can span live and backtest
      if (reflectionToView) {
        array = [...(auth.journalsBacktesting || []), ...(auth.journals || [])];
      } else {
        array = [...auth.journalsBacktesting];
      }

      // Filter by missed trades
      if (missedTradeFilter) {
        const missedTradeIds = (auth.trades || [])
          .filter((t) => {
            // Handle both "Missed Trades" and "All Missed Trades" as testId values
            const isMissedTest =
              testId === "Missed Trades" || testId === "All Missed Trades";
            const matchesTestId = isMissedTest
              ? t.testId === "Missed Trades"
              : t.testId === testId;
            const matchesMissedStatus =
              missedTradeFilter === "Yes"
                ? t.missedTrade === true
                : t.missedTrade !== true;
            return matchesTestId && matchesMissedStatus;
          })
          .map((t) => t.journalID);

        // Only apply filter if we have missed trade IDs to filter by
        if (missedTradeIds.length > 0) {
          array = array.filter((j) => missedTradeIds.includes(j.journalID));
        }
      }

      // If opened from reflection, filter to reflection's trades
      if (reflectionToView) {
        const reflectionTradeIds = JSON.parse(
          localStorage.getItem("reflection-trade-ids") || "[]",
        );
        const reflectionJournalIds = (auth.trades || [])
          .filter((t) => reflectionTradeIds.includes(t._id))
          .map((t) => t.journalID);
        array = array.filter((j) => reflectionJournalIds.includes(j.journalID));
      }
    } else {
      // Live mode - use existing logic
      const selectedAccountId = localStorage.getItem("selected-account-id");
      const yearFilter = localStorage.getItem("year");
      let trades = auth.trades;
      let journalIds;

      trades = trades.filter((tests) => tests?.id?.includes("live-"));

      // Filter by year if set
      if (yearFilter && yearFilter !== "0") {
        const currentYearSet = moment(yearFilter).format("YY");
        trades = trades.filter((t) => {
          if (!t.entryDate) return false;
          let dateArray = t.entryDate.split("/");
          let tradeYear = dateArray[2];
          return Number(tradeYear) === Number(currentYearSet);
        });
      }

      // Filter by account ID
      if (selectedAccountId && selectedAccountId !== "All") {
        if (selectedAccountId === "All Personal") {
          trades = trades.filter(
            (trade) => trade.equityType === "Personal Capital",
          );
        } else if (selectedAccountId === "All Investor") {
          trades = trades.filter(
            (trade) => trade.equityType === "Investor Capital",
          );
        } else {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          const capitalAccounts = user.capitalAccounts || [];
          const selectedAccount = capitalAccounts.find(
            (acc) => acc.id === selectedAccountId,
          );
          if (selectedAccount) {
            trades = trades.filter((trade) => {
              if (trade.accountId) return trade.accountId === selectedAccountId;
              if (selectedAccount.type === "Personal")
                return trade.equityType === "Personal Capital";
              if (selectedAccount.type === "Investor")
                return trade.equityType === "Investor Capital";
              return false;
            });
          }
        }
      }

      // If opened from reflection, filter to reflection's trades
      if (reflectionToView) {
        const reflectionTradeIds = JSON.parse(
          localStorage.getItem("reflection-trade-ids") || "[]",
        );
        if (reflectionTradeIds.length > 0) {
          trades = trades.filter((trade) =>
            reflectionTradeIds.includes(trade._id),
          );
        }
      }

      journalIds = trades.map((t) => t.journalID).filter((id) => id);
      array = auth.journals.filter((j) => journalIds.includes(j.journalID));
    }

    if (array.length === 0) {
      return;
    }

    if (array.length === 1) {
      return;
    }

    if (array.length > 1) {
      // Sort journals by their corresponding trade's entryDate (oldest first)
      array = array.sort((a, b) => {
        const tradeA = (auth.trades || []).find(
          (t) => t.journalID === a.journalID,
        );
        const tradeB = (auth.trades || []).find(
          (t) => t.journalID === b.journalID,
        );

        if (!tradeA || !tradeB) return 0;

        const dateA = moment(tradeA.entryDate, "DD/MM/YY");
        const dateB = moment(tradeB.entryDate, "DD/MM/YY");
        return dateA.diff(dateB); // Oldest first
      });

      const foundIndex = array.findIndex(
        (x) => x.journalID === pairSelected.journalID,
      );

      next = foundIndex + 1;
      back = foundIndex - 1;

      // Stop at the ends of the list - no wrap-around
      if (foundIndex === 0) {
        back = -1; // No older trade
      }
      if (foundIndex + 1 === array.length) {
        next = -1; // No newer trade
      }

      if (direction === "Next") {
        // Stop if at the end of the list
        if (next === -1 || next >= array.length) {
          return;
        }

        // Try to find trade with calculated fields from allTrades first, fallback to trades
        let trade = auth.allTrades?.find(
          (t) => t.journalID === array[next]?.journalID,
        );

        if (!trade) {
          trade = auth.trades.find(
            (t) => t.journalID === array[next]?.journalID,
          );
        }

        if (!trade) {
          console.error("Trade not found for journal:", array[next]?.journalID);
          return;
        }

        // Fetch fresh trade data from server to get updated netDollar values
        const freshData = await fetchFreshTradeData(trade._id);

        // Merge fresh data with local trade data, prioritizing fresh data
        trade = freshData ? { ...trade, ...freshData } : trade;

        checkTypeOfpair(trade.pair);

        // Use DB-calculated outcomes (all trades have these after migration)
        const outcomeOfSlippagePC = trade.outcomePC;
        const outcomeOfSlippageIC = trade.outcomeIC;

        // Create a new object to avoid mutating auth.trades
        const updatedTrade = {
          ...trade,
          outcomePC: outcomeOfSlippagePC,
          outcomeIC: outcomeOfSlippageIC,
        };

        setPairSelected(updatedTrade);

        localStorage.setItem(
          "journal-portal",
          JSON.stringify({
            journalID: array[next]?.journalID,
            trade_id: trade._id,
          }),
        );

        localStorage.setItem("trade-portal", JSON.stringify(trade));

        setPros(array[next]?.pros);
        setCons(array[next]?.cons ? array[next]?.cons : "");
        setEmotions(array[next]?.emotions ? array[next]?.emotions : "");
        setPlan(array[next]?.plan ? array[next]?.plan : "");
        setTakeAgain(array[next]?.takeAgain ? array[next]?.takeAgain : "");
        setExtraNotes(array[next]?.extraNotes ? array[next]?.extraNotes : "");
        setLinkedForecast(
          array[next]?.foreCastId ? array[next]?.foreCastId : "",
        );
        setMin5_beforeNote(
          array[next]?.min5_beforeNote ? array[next]?.min5_beforeNote : "",
        );
        setMin15_beforeNote(
          array[next]?.min15_beforeNote ? array[next]?.min15_beforeNote : "",
        );
        setMin5_afterNote(
          array[next]?.min5_afterNote ? array[next]?.min5_afterNote : "",
        );
        setMin15_afterNote(
          array[next]?.min15_afterNote ? array[next]?.min15_afterNote : "",
        );
        setOneHour_beforeNote(
          array[next]?.oneHour_beforeNote
            ? array[next]?.oneHour_beforeNote
            : "",
        );
        setOneHour_afterNote(
          array[next]?.oneHour_afterNote ? array[next]?.oneHour_afterNote : "",
        );
        setFourHour_afterNote(
          array[next]?.fourHour_afterNote
            ? array[next]?.fourHour_afterNote
            : "",
        );
        setFourHour_beforeNote(
          array[next]?.fourHour_beforeNote
            ? array[next]?.fourHour_beforeNote
            : "",
        );
        setDaily_beforeNote(
          array[next]?.daily_beforeNote ? array[next]?.daily_beforeNote : "",
        );
        setDaily_afterNote(
          array[next]?.daily_afterNote ? array[next]?.daily_afterNote : "",
        );
        setWeekly_beforeNote(
          array[next]?.weekly_beforeNote ? array[next]?.weekly_beforeNote : "",
        );
        setWeekly_afterNote(
          array[next]?.weekly_afterNote ? array[next]?.weekly_afterNote : "",
        );

        setDxyHourlyBeforeNote(
          array[next]?.dxyHourlyBeforeNote
            ? array[next]?.dxyHourlyBeforeNote
            : "",
        );
        setDxyHourlyAfterNote(
          array[next]?.dxyHourlyAfterNote
            ? array[next]?.dxyHourlyAfterNote
            : "",
        );
        setDxyDailyBeforeNote(
          array[next]?.dxyDailyBeforeNote
            ? array[next]?.dxyDailyBeforeNote
            : "",
        );
        setDxyDailyAfterNote(
          array[next]?.dxyDailyAfterNote ? array[next]?.dxyDailyAfterNote : "",
        );
        setExecutionDiscipline(trade.executionDiscipline || 0);
        setEmotionalControl(trade.emotionalControl || 0);
        setSetupQuality(trade.setupQuality || 0);
        setExecutionRight(array[next]?.executionRight || "");
        setExecutionDeviate(array[next]?.executionDeviate || "");
        setSystemAppropriate(array[next]?.systemAppropriate || "");
        setSystemWhy(array[next]?.systemWhy || "");
        setEmotionsBefore(array[next]?.emotionsBefore || "");
        setEmotionsWhile(array[next]?.emotionsWhile || "");
        setEmotionsAfter(array[next]?.emotionsAfter || "");
      } else if (direction === "Back") {
        // Stop if at the start of the list
        if (back === -1 || back < 0) {
          return;
        }

        // Try to find trade with calculated fields from allTrades first, fallback to trades
        let trade = auth.allTrades?.find(
          (t) => t.journalID === array[back]?.journalID,
        );

        if (!trade) {
          trade = auth.trades.find(
            (t) => t.journalID === array[back]?.journalID,
          );
        }

        if (!trade) {
          console.error("Trade not found for journal:", array[back]?.journalID);
          return;
        }

        // Fetch fresh trade data from server to get updated netDollar values
        const freshData = await fetchFreshTradeData(trade._id);

        // Merge fresh data with local trade data, prioritizing fresh data
        trade = freshData ? { ...trade, ...freshData } : trade;

        checkTypeOfpair(trade.pair);

        // Use DB-calculated outcomes (all trades have these after migration)
        const outcomeOfSlippagePC = trade.outcomePC;
        const outcomeOfSlippageIC = trade.outcomeIC;

        // Create a new object to avoid mutating auth.trades
        const updatedTrade = {
          ...trade,
          outcomePC: outcomeOfSlippagePC,
          outcomeIC: outcomeOfSlippageIC,
        };

        setPairSelected(updatedTrade);

        localStorage.setItem(
          "journal-portal",
          JSON.stringify({
            journalID: array[back]?.journalID,
            trade_id: trade._id,
          }),
        );

        localStorage.setItem("trade-portal", JSON.stringify(trade));

        setPros(array[back]?.pros);
        setCons(array[back]?.cons ? array[back]?.cons : "");
        setEmotions(array[back]?.emotions ? array[back]?.emotions : "");
        setPlan(array[back]?.plan ? array[back]?.plan : "");
        setTakeAgain(array[back]?.takeAgain ? array[back]?.takeAgain : "");
        setExtraNotes(array[back]?.extraNotes ? array[back]?.extraNotes : "");
        setLinkedForecast(
          array[back]?.foreCastId ? array[back]?.foreCastId : "",
        );
        setMin5_beforeNote(
          array[back]?.min5_beforeNote ? array[back]?.min5_beforeNote : "",
        );
        setMin15_beforeNote(
          array[back]?.min15_beforeNote ? array[back]?.min15_beforeNote : "",
        );
        setMin5_afterNote(
          array[back]?.min5_afterNote ? array[back]?.min5_afterNote : "",
        );
        setMin15_afterNote(
          array[back]?.min15_afterNote ? array[back]?.min15_afterNote : "",
        );
        setOneHour_beforeNote(
          array[back]?.oneHour_beforeNote
            ? array[back]?.oneHour_beforeNote
            : "",
        );
        setOneHour_afterNote(
          array[back]?.oneHour_afterNote ? array[back]?.oneHour_afterNote : "",
        );
        setFourHour_afterNote(
          array[back]?.fourHour_afterNote
            ? array[back]?.fourHour_afterNote
            : "",
        );
        setFourHour_beforeNote(
          array[back]?.fourHour_beforeNote
            ? array[back]?.fourHour_beforeNote
            : "",
        );
        setDaily_beforeNote(
          array[back]?.daily_beforeNote ? array[back]?.daily_beforeNote : "",
        );
        setDaily_afterNote(
          array[back]?.daily_afterNote ? array[back]?.daily_afterNote : "",
        );
        setWeekly_beforeNote(
          array[back]?.weekly_beforeNote ? array[back]?.weekly_beforeNote : "",
        );
        setWeekly_afterNote(
          array[back]?.weekly_afterNote ? array[back]?.weekly_afterNote : "",
        );

        setDxyHourlyBeforeNote(
          array[back]?.dxyHourlyBeforeNote
            ? array[back]?.dxyHourlyBeforeNote
            : "",
        );
        setDxyHourlyAfterNote(
          array[back]?.dxyHourlyAfterNote
            ? array[back]?.dxyHourlyAfterNote
            : "",
        );
        setDxyDailyBeforeNote(
          array[back]?.dxyDailyBeforeNote
            ? array[back]?.dxyDailyBeforeNote
            : "",
        );
        setDxyDailyAfterNote(
          array[back]?.dxyDailyAfterNote ? array[back]?.dxyDailyAfterNote : "",
        );
        setExecutionDiscipline(trade.executionDiscipline || 0);
        setEmotionalControl(trade.emotionalControl || 0);
        setSetupQuality(trade.setupQuality || 0);
        setExecutionRight(array[back]?.executionRight || "");
        setExecutionDeviate(array[back]?.executionDeviate || "");
        setSystemAppropriate(array[back]?.systemAppropriate || "");
        setSystemWhy(array[back]?.systemWhy || "");
        setEmotionsBefore(array[back]?.emotionsBefore || "");
        setEmotionsWhile(array[back]?.emotionsWhile || "");
        setEmotionsAfter(array[back]?.emotionsAfter || "");
      }
    }
  };

  return (
    !isLoading && (
      <Modal
        open={open}
        onClose={onClose}
        BackdropProps={{ invisible: true }}
        disableBackdropClick
      >
        <>
          <Box
            ref={scrollBoxRef}
            sx={{
              overflowY: !isViewerOpen ? "auto" : null,
              overflowX: !isViewerOpen ? "hidden" : null,
              position: !isViewerOpen ? "absolute" : "fixed",
              top: "57px",
              left: "50%",
              transform: "translateX(-50%)",
              color: theme[0] !== "dark" ? "#374151" : "#e5e7eb",
              width: "100%",
              maxWidth: "1920px",
              height: "calc(100vh - 57px)",
              backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
              padding: { xs: "10px 15px 50px 15px", sm: "10px 15px 15px 15px" },
              margin: "0 auto",
            }}
          >
            {isViewerOpen && (
              <EnhancedImageLightbox
                images={urls}
                labels={urls.map((_, index) => getTitle(index))}
                currentIndex={currentImage}
                onClose={closeImageViewer}
                onNavigate={(index) => setCurrentImage(index)}
              />
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                margin: width <= 800 ? "1em 1.5em 0 1.5em" : "1em 2em 0 2em",
                gap: "1em",
              }}
            >
              <div
                onClick={() =>
                  changeJournal(
                    "Back",
                    !auth.backtestingMode
                      ? auth.journals
                      : auth.journalsBacktesting,
                  )
                }
                onMouseEnter={() => setIsHoveringBack(true)}
                onMouseLeave={() => setIsHoveringBack(false)}
                style={{
                  cursor: "pointer",
                  padding: "8px 16px",
                  borderRadius: "20px",
                  backgroundColor: isHoveringBack
                    ? theme[0] === "dark"
                      ? "rgba(237, 158, 14, 0.15)"
                      : "rgba(237, 158, 14, 0.1)"
                    : theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.1)"
                      : "rgba(107, 114, 128, 0.05)",
                  border: `2px solid ${
                    isHoveringBack
                      ? "#ED9E0E"
                      : theme[0] === "dark"
                        ? "#2d2e33"
                        : "#e5e7eb"
                  }`,
                  color: isHoveringBack
                    ? "#ED9E0E"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280",
                  fontWeight: "600",
                  fontSize: "14px",
                  letterSpacing: "0.5px",
                  transition: "all 0.3s ease",
                  transform: isHoveringBack
                    ? "translateY(-2px)"
                    : "translateY(0)",
                }}
              >
                ← OLDER
              </div>
              <div
                onMouseEnter={() => setIsHoveringNext(true)}
                onMouseLeave={() => setIsHoveringNext(false)}
                onClick={() =>
                  changeJournal(
                    "Next",
                    !auth.backtestingMode
                      ? auth.journals
                      : auth.journalsBacktesting,
                  )
                }
                style={{
                  cursor: "pointer",
                  padding: "8px 16px",
                  borderRadius: "20px",
                  backgroundColor: isHoveringNext
                    ? theme[0] === "dark"
                      ? "rgba(237, 158, 14, 0.15)"
                      : "rgba(237, 158, 14, 0.1)"
                    : theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.1)"
                      : "rgba(107, 114, 128, 0.05)",
                  border: `2px solid ${
                    isHoveringNext
                      ? "#ED9E0E"
                      : theme[0] === "dark"
                        ? "#2d2e33"
                        : "#e5e7eb"
                  }`,
                  color: isHoveringNext
                    ? "#ED9E0E"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280",
                  fontWeight: "600",
                  fontSize: "14px",
                  letterSpacing: "0.5px",
                  transition: "all 0.3s ease",
                  transform: isHoveringNext
                    ? "translateY(-2px)"
                    : "translateY(0)",
                }}
              >
                NEWER →
              </div>
            </div>

            <Grid
              container
              direction="row"
              justifyContent="space-evenly"
              alignItems="center"
              spacing={0}
              sx={{ maxWidth: "95%", margin: "0 auto", padding: "0" }}
            >
              <Grid item xs={12}>
                <div
                  style={{
                    maxWidth: "100%",
                    margin:
                      width <= 768
                        ? "0.5em auto 0.5em auto"
                        : "1em auto 0.5em auto",
                    marginBottom: width <= 768 ? "2.5em" : "2em",
                    padding: width <= 768 ? "16px 20px" : "20px 32px",
                    borderRadius: "16px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    boxShadow:
                      theme[0] === "dark"
                        ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                        : "0 2px 8px rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    {pairSelected ? (
                      <>
                        <span
                          style={{
                            fontSize: "23px",
                            fontWeight: "600",
                            letterSpacing: "0.5px",
                            color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                          }}
                        >
                          {pairSelected.pair}
                        </span>

                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: "700",
                            letterSpacing: "0.5px",
                            padding: "3px 8px",
                            borderRadius: "6px",
                            backgroundColor:
                              pairSelected.direction === "Short" ||
                              pairSelected.optionType === "Put"
                                ? theme[0] === "dark"
                                  ? "rgba(219, 6, 113, 0.15)"
                                  : "rgba(219, 6, 113, 0.1)"
                                : pairSelected.direction === "Long" ||
                                    pairSelected.optionType === "Call"
                                  ? theme[0] === "dark"
                                    ? "rgba(38, 166, 93, 0.15)"
                                    : "rgba(38, 166, 93, 0.1)"
                                  : theme[0] === "dark"
                                    ? "rgba(237, 158, 14, 0.15)"
                                    : "rgba(237, 158, 14, 0.1)",
                            border: `2px solid ${
                              pairSelected.direction === "Short" ||
                              pairSelected.optionType === "Put"
                                ? "#db0671"
                                : pairSelected.direction === "Long" ||
                                    pairSelected.optionType === "Call"
                                  ? "#26a65d"
                                  : "#ED9E0E"
                            }`,
                            color:
                              pairSelected.direction === "Short" ||
                              pairSelected.optionType === "Put"
                                ? "#db0671"
                                : pairSelected.direction === "Long" ||
                                    pairSelected.optionType === "Call"
                                  ? "#26a65d"
                                  : "#ED9E0E",
                          }}
                        >
                          {pairSelected.direction
                            ? pairSelected.direction
                            : pairSelected.optionType}
                        </span>

                        {(pairSelected.equityType === "Personal Capital" ||
                          pairSelected.equityType === "Backtest") && (
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              letterSpacing: "0.5px",
                              padding: "3px 8px",
                              borderRadius: "6px",
                              backgroundColor:
                                pairSelected.outcomePC === "Loss"
                                  ? theme[0] === "dark"
                                    ? "rgba(242, 54, 69, 0.15)"
                                    : "rgba(242, 54, 69, 0.1)"
                                  : pairSelected.outcomePC === "Win"
                                    ? theme[0] === "dark"
                                      ? "rgba(38, 166, 93, 0.15)"
                                      : "rgba(38, 166, 93, 0.1)"
                                    : theme[0] === "dark"
                                      ? "rgba(237, 158, 14, 0.15)"
                                      : "rgba(237, 158, 14, 0.1)",
                              border: `2px solid ${
                                pairSelected.outcomePC === "Loss"
                                  ? "#f23645"
                                  : pairSelected.outcomePC === "Win"
                                    ? "#26a65d"
                                    : "#ED9E0E"
                              }`,
                              color:
                                pairSelected.outcomePC === "Loss"
                                  ? "#f23645"
                                  : pairSelected.outcomePC === "Win"
                                    ? "#26a65d"
                                    : "#ED9E0E",
                            }}
                          >
                            {pairSelected.outcomePC}
                          </span>
                        )}

                        {pairSelected.equityType === "Investor Capital" && (
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              letterSpacing: "0.5px",
                              padding: "3px 8px",
                              borderRadius: "6px",
                              backgroundColor:
                                pairSelected.outcomeIC === "Loss"
                                  ? theme[0] === "dark"
                                    ? "rgba(242, 54, 69, 0.15)"
                                    : "rgba(242, 54, 69, 0.1)"
                                  : pairSelected.outcomeIC === "Win"
                                    ? theme[0] === "dark"
                                      ? "rgba(38, 166, 93, 0.15)"
                                      : "rgba(38, 166, 93, 0.1)"
                                    : theme[0] === "dark"
                                      ? "rgba(237, 158, 14, 0.15)"
                                      : "rgba(237, 158, 14, 0.1)",
                              border: `2px solid ${
                                pairSelected.outcomeIC === "Loss"
                                  ? "#f23645"
                                  : pairSelected.outcomeIC === "Win"
                                    ? "#26a65d"
                                    : "#ED9E0E"
                              }`,
                              color:
                                pairSelected.outcomeIC === "Loss"
                                  ? "#f23645"
                                  : pairSelected.outcomeIC === "Win"
                                    ? "#26a65d"
                                    : "#ED9E0E",
                            }}
                          >
                            {pairSelected.outcomeIC}
                          </span>
                        )}

                        <span
                          style={{
                            fontSize: "18px",
                            fontWeight: "400",
                            letterSpacing: "0.3px",
                            color:
                              theme[0] === "dark"
                                ? "rgba(252, 252, 255, 0.7)"
                                : "rgba(26, 27, 30, 0.7)",
                          }}
                        >
                          {pairSelected.entryDate &&
                            moment(
                              formatDateTimeInTimezone(
                                pairSelected.entryDate,
                                pairSelected.entryTime,
                                pairSelected.timezone,
                              ),
                              "DD/MM/YY",
                            ).format("ddd Do MMM YYYY")}
                        </span>
                      </>
                    ) : null}
                  </div>

                  <hr
                    style={{
                      width: "100%",
                      border: "none",
                      borderTop: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                      margin: width <= 768 ? "12px 0 10px 0" : "20px 0 16px 0",
                    }}
                  />

                  {(() => {
                    // Calculate gross/net values
                    const usePC =
                      pairSelected?.equityType === "Personal Capital" ||
                      pairSelected?.equityType === "Backtest";
                    const grossPercent = usePC
                      ? pairSelected?.profitLossPercentagePC
                      : pairSelected?.profitLossPercentageIC;
                    const netPercent = usePC
                      ? pairSelected?.percentWithFeesPC
                      : pairSelected?.percentWithFeesIC;
                    const grossDollar = usePC
                      ? pairSelected?.profitLossDollarPersonal
                      : pairSelected?.profitLossDollarInvestor;
                    // const netDollar = usePC ? (pairSelected?.netDollarPC || 0) : (pairSelected?.netDollarIC || 0);

                    // Use showGross to determine which values to display
                    const displayPercent = showGross ? grossPercent : netPercent;
                    const displayDollar = showGross ? grossDollar : (usePC
                      ? (pairSelected?.netDollarPC !== undefined ? pairSelected?.netDollarPC : (parseFloat(pairSelected?.profitLossDollarPersonal || 0) + parseFloat(pairSelected?.feePC || 0) + parseFloat(pairSelected?.swapPC || 0)))
                      : (pairSelected?.netDollarIC !== undefined ? pairSelected?.netDollarIC : (parseFloat(pairSelected?.profitLossDollarInvestor || 0) + parseFloat(pairSelected?.feeIC || 0) + parseFloat(pairSelected?.swapIC || 0))));

                    // Get forecast data if available
                    let positioning,
                      sequence,
                      forecastGrade,
                      tradeGrade,
                      linkedForecastData;
                    if (linkedForecast && linkedForecast !== "") {
                      const forecastArray = auth.backtestingMode
                        ? auth.forecastsBacktesting
                        : auth.forecasts;
                      linkedForecastData = forecastArray.find(
                        (f) => f._id === linkedForecast,
                      );
                      if (linkedForecastData) {
                        positioning = linkedForecastData.positioning;
                        sequence = linkedForecastData.sequence;
                        forecastGrade = linkedForecastData.grade;
                        tradeGrade = pairSelected?.grade;
                      }
                    }

                    // Check if we should display anything
                    const hasGrossNet =
                      (grossPercent !== undefined && grossPercent !== null) ||
                      (netPercent !== undefined && netPercent !== null) ||
                      pairSelected?.outcomePC;
                    const hasForecastData =
                      positioning || sequence || tradeGrade || forecastGrade;

                    if (!hasGrossNet && !hasForecastData) return null;

                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent:
                            width < 768 ? "center" : "space-evenly",
                          alignItems: "center",
                          gap: width < 768 ? "8px" : "12px",
                          flexWrap: "wrap",
                          marginTop: width <= 768 ? "12px" : "16px",
                          maxWidth: "100%",
                        }}
                      >
                        {/* Gross/Net Badges */}
                        {hasGrossNet &&
                          (displayPercent !== undefined ||
                            displayPercent !== null) && (
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                flexWrap: "wrap",
                                justifyContent: "center",
                              }}
                            >
                              {/* GROSS/NET Badge */}
                              {displayPercent !== undefined &&
                                displayPercent !== null &&
                                !isNaN(displayPercent) && (
                                  <div
                                    style={{
                                      position: "relative",
                                      padding: "4px 10px",
                                      borderRadius: "12px",
                                      backgroundColor:
                                        displayPercent > 0
                                          ? theme[0] === "dark"
                                            ? "rgba(38, 166, 93, 0.15)"
                                            : "rgba(38, 166, 93, 0.1)"
                                          : displayPercent < 0
                                            ? theme[0] === "dark"
                                              ? "rgba(242, 54, 69, 0.15)"
                                              : "rgba(242, 54, 69, 0.1)"
                                            : theme[0] === "dark"
                                              ? "rgba(237, 158, 14, 0.15)"
                                              : "rgba(237, 158, 14, 0.1)",
                                      border: `2px solid ${
                                        displayPercent > 0
                                          ? "#26a65d"
                                          : displayPercent < 0
                                            ? "#f23645"
                                            : "#ED9E0E"
                                      }`,
                                      minWidth: "auto",
                                      textAlign: "center",
                                    }}
                                  >
                                    <p
                                      style={{
                                        fontSize: "8px",
                                        fontWeight: "600",
                                        letterSpacing: "0.8px",
                                        color:
                                          theme[0] !== "dark"
                                            ? "#6b7280"
                                            : "#9ca3af",
                                        margin: "0 0 2px 0",
                                      }}
                                    >
                                      {showGross ? "GROSS" : "NET"}
                                    </p>
                                    <p
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        letterSpacing: "0.3px",
                                        margin: 0,
                                        color:
                                          displayPercent > 0
                                            ? "#26a65d"
                                            : displayPercent < 0
                                              ? "#f23645"
                                              : "#ED9E0E",
                                      }}
                                    >
                                      {displayPercent !== 0
                                        ? `${displayPercent > 0 ? "+" : ""}${Math.abs(displayPercent).toFixed(2)}% / ${getCurrency()}${Math.abs(displayDollar || 0).toFixed(2)}`
                                        : `0% / ${getCurrency()}${Math.abs(displayDollar || 0).toFixed(2)}`}
                                    </p>
                                  </div>
                                )}

                              {/* RISK Badge */}
                              {pairSelected.risk && (() => {
                                const riskValue = parseFloat(pairSelected.risk);
                                let riskColor;
                                if (riskValue <= 1) {
                                  riskColor = "#26a65d";
                                } else if (riskValue > 1 && riskValue <= 1.1) {
                                  riskColor = "#ED9E0E";
                                } else {
                                  riskColor = "#f23645";
                                }
                                return (
                                  <div
                                    style={{
                                      position: "relative",
                                      padding: "4px 10px",
                                      borderRadius: "12px",
                                      backgroundColor:
                                        theme[0] === "dark"
                                          ? riskColor === "#26a65d"
                                            ? "rgba(38, 166, 93, 0.15)"
                                            : riskColor === "#ED9E0E"
                                              ? "rgba(237, 158, 14, 0.15)"
                                              : "rgba(242, 54, 69, 0.15)"
                                          : riskColor === "#26a65d"
                                            ? "rgba(38, 166, 93, 0.1)"
                                            : riskColor === "#ED9E0E"
                                              ? "rgba(237, 158, 14, 0.1)"
                                              : "rgba(242, 54, 69, 0.1)",
                                      border: `2px solid ${riskColor}`,
                                      minWidth: "auto",
                                      textAlign: "center",
                                    }}
                                  >
                                    <p
                                      style={{
                                        fontSize: "8px",
                                        fontWeight: "600",
                                        letterSpacing: "0.8px",
                                        color:
                                          theme[0] !== "dark"
                                            ? "#6b7280"
                                            : "#9ca3af",
                                        margin: "0 0 2px 0",
                                      }}
                                    >
                                      RISK
                                    </p>
                                    <p
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        letterSpacing: "0.3px",
                                        margin: 0,
                                        color: riskColor,
                                      }}
                                    >
                                      {riskValue === 1 ? "1" : riskValue.toFixed(2)}% / {getCurrency()}
                                      {(
                                        pairSelected.equityType === "Personal Capital" ||
                                        pairSelected.equityType === "Backtest"
                                          ? pairSelected.moneyPC
                                          : pairSelected.moneyIC
                                      )?.toFixed(2) || "0.00"}
                                    </p>
                                  </div>
                                );
                              })()}

                              {/* NET Badge */}
                              {/* {(netPercent !== undefined && netPercent !== null && !isNaN(netPercent)) && (
                            <div
                              style={{
                                position: "relative",
                                padding: "6px 12px",
                                borderRadius: "16px",
                                backgroundColor: netPercent > 0
                                  ? theme[0] === "dark" ? "rgba(38, 166, 93, 0.15)" : "rgba(38, 166, 93, 0.1)"
                                  : netPercent < 0
                                  ? theme[0] === "dark" ? "rgba(242, 54, 69, 0.15)" : "rgba(242, 54, 69, 0.1)"
                                  : theme[0] === "dark" ? "rgba(237, 158, 14, 0.15)" : "rgba(237, 158, 14, 0.1)",
                                border: `2px solid ${
                                  netPercent > 0
                                    ? "#26a65d"
                                    : netPercent < 0
                                    ? "#f23645"
                                    : "#ED9E0E"
                                }`,
                                minWidth: "auto",
                                textAlign: "center",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "9px",
                                  fontWeight: "600",
                                  letterSpacing: "0.8px",
                                  color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                                  margin: "0 0 2px 0",
                                }}
                              >
                                NET
                              </p>
                              <p
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  letterSpacing: "0.3px",
                                  margin: 0,
                                  color: netPercent > 0
                                    ? "#26a65d"
                                    : netPercent < 0
                                    ? "#f23645"
                                    : "#ED9E0E",
                                }}
                              >
                                {netPercent !== 0
                                  ? `${netPercent > 0 ? "+" : ""}${Math.abs(netPercent).toFixed(2)}% / $${Math.abs(netDollar || 0).toFixed(2)}`
                                  : `0% / $${Math.abs(netDollar || 0).toFixed(2)}`}
                              </p>
                            </div>
                          )} */}
                            </div>
                          )}

                        {/* Forecast-related badges */}
                        {linkedForecastData &&
                          (() => {
                            // Check if forecast risk differs from trade risk
                            const riskDiffers =
                              forecastGrade &&
                              tradeGrade &&
                              forecastGrade !== tradeGrade;

                            return (
                              <>
                                {(positioning || sequence) && (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "8px",
                                      flexWrap: "wrap",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {positioning && (
                                      <div
                                        style={{
                                          position: "relative",
                                          padding: "4px 10px",
                                          borderRadius: "12px",
                                          backgroundColor:
                                            positioning ===
                                            "Excellent Positioning"
                                              ? theme[0] === "dark"
                                                ? "rgba(38, 166, 93, 0.15)"
                                                : "rgba(38, 166, 93, 0.1)"
                                              : positioning ===
                                                  "Average Positioning"
                                                ? theme[0] === "dark"
                                                  ? "rgba(237, 158, 14, 0.15)"
                                                  : "rgba(237, 158, 14, 0.1)"
                                                : theme[0] === "dark"
                                                  ? "rgba(242, 54, 69, 0.15)"
                                                  : "rgba(242, 54, 69, 0.1)",
                                          border: `2px solid ${
                                            positioning ===
                                            "Excellent Positioning"
                                              ? "#26a65d"
                                              : positioning ===
                                                  "Average Positioning"
                                                ? "#ED9E0E"
                                                : "#f23645"
                                          }`,
                                          minWidth:
                                            width < 768 ? "100px" : "120px",
                                          maxWidth:
                                            width < 768 ? "140px" : "160px",
                                          textAlign: "center",
                                        }}
                                      >
                                        <p
                                          style={{
                                            fontSize: "8px",
                                            fontWeight: "600",
                                            letterSpacing: "0.8px",
                                            color:
                                              theme[0] !== "dark"
                                                ? "#6b7280"
                                                : "#9ca3af",
                                            margin: "0 0 2px 0",
                                          }}
                                        >
                                          POSITIONING
                                        </p>
                                        <p
                                          style={{
                                            fontSize: "12px",
                                            fontWeight: "600",
                                            letterSpacing: "0.3px",
                                            margin: 0,
                                            color:
                                              positioning ===
                                              "Excellent Positioning"
                                                ? "#26a65d"
                                                : positioning ===
                                                    "Average Positioning"
                                                  ? "#ED9E0E"
                                                  : "#f23645",
                                          }}
                                        >
                                          {positioning
                                            .replace(" Positioning", "")
                                            .toUpperCase()}
                                        </p>
                                      </div>
                                    )}
                                    {sequence && (
                                      <div
                                        style={{
                                          position: "relative",
                                          padding: "4px 10px",
                                          borderRadius: "12px",
                                          backgroundColor:
                                            sequence === "Excellent Sequence"
                                              ? theme[0] === "dark"
                                                ? "rgba(38, 166, 93, 0.15)"
                                                : "rgba(38, 166, 93, 0.1)"
                                              : sequence === "Average Sequence"
                                                ? theme[0] === "dark"
                                                  ? "rgba(237, 158, 14, 0.15)"
                                                  : "rgba(237, 158, 14, 0.1)"
                                                : theme[0] === "dark"
                                                  ? "rgba(242, 54, 69, 0.15)"
                                                  : "rgba(242, 54, 69, 0.1)",
                                          border: `2px solid ${
                                            sequence === "Excellent Sequence"
                                              ? "#26a65d"
                                              : sequence === "Average Sequence"
                                                ? "#ED9E0E"
                                                : "#f23645"
                                          }`,
                                          minWidth:
                                            width < 768 ? "100px" : "120px",
                                          maxWidth:
                                            width < 768 ? "140px" : "160px",
                                          textAlign: "center",
                                        }}
                                      >
                                        <p
                                          style={{
                                            fontSize: "8px",
                                            fontWeight: "600",
                                            letterSpacing: "0.8px",
                                            color:
                                              theme[0] !== "dark"
                                                ? "#6b7280"
                                                : "#9ca3af",
                                            margin: "0 0 2px 0",
                                          }}
                                        >
                                          SEQUENCE
                                        </p>
                                        <p
                                          style={{
                                            fontSize: "12px",
                                            fontWeight: "600",
                                            letterSpacing: "0.3px",
                                            margin: 0,
                                            color:
                                              sequence === "Excellent Sequence"
                                                ? "#26a65d"
                                                : sequence ===
                                                    "Average Sequence"
                                                  ? "#ED9E0E"
                                                  : "#f23645",
                                          }}
                                        >
                                          {sequence
                                            .replace(" Sequence", "")
                                            .toUpperCase()}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {riskDiffers ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "8px",
                                      flexWrap: "wrap",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {forecastGrade && (
                                      <div
                                        style={{
                                          position: "relative",
                                          padding: "4px 10px",
                                          borderRadius: "12px",
                                          backgroundColor:
                                            forecastGrade === "Low Risk"
                                              ? theme[0] === "dark"
                                                ? "rgba(38, 166, 93, 0.15)"
                                                : "rgba(38, 166, 93, 0.1)"
                                              : forecastGrade === "High Risk"
                                                ? theme[0] === "dark"
                                                  ? "rgba(255, 165, 0, 0.15)"
                                                  : "rgba(255, 165, 0, 0.1)"
                                                : forecastGrade === "Invalid"
                                                  ? theme[0] === "dark"
                                                    ? "rgba(242, 54, 69, 0.15)"
                                                    : "rgba(242, 54, 69, 0.1)"
                                                  : theme[0] === "dark"
                                                    ? "rgba(242, 54, 69, 0.15)"
                                                    : "rgba(242, 54, 69, 0.1)",
                                          border: `2px solid ${
                                            forecastGrade === "Low Risk"
                                              ? "#26a65d"
                                              : forecastGrade === "High Risk"
                                                ? "#FFA500"
                                                : forecastGrade === "Invalid"
                                                  ? "#f23645"
                                                  : "#f23645"
                                          }`,
                                          minWidth:
                                            width < 768 ? "100px" : "120px",
                                          maxWidth:
                                            width < 768 ? "140px" : "160px",
                                          textAlign: "center",
                                        }}
                                      >
                                        <p
                                          style={{
                                            fontSize: "8px",
                                            fontWeight: "600",
                                            letterSpacing: "0.8px",
                                            color:
                                              theme[0] !== "dark"
                                                ? "#6b7280"
                                                : "#9ca3af",
                                            margin: "0 0 2px 0",
                                          }}
                                        >
                                          FORECAST
                                        </p>
                                        <p
                                          style={{
                                            fontSize: "12px",
                                            fontWeight: "600",
                                            letterSpacing: "0.3px",
                                            margin: 0,
                                            color:
                                              forecastGrade === "Low Risk"
                                                ? "#26a65d"
                                                : forecastGrade === "High Risk"
                                                  ? "#FFA500"
                                                  : forecastGrade === "Invalid"
                                                    ? "#f23645"
                                                    : "#f23645",
                                          }}
                                        >
                                          {forecastGrade
                                            .replace(" Risk", "")
                                            .toUpperCase()}
                                        </p>
                                      </div>
                                    )}
                                    {tradeGrade && (
                                      <div
                                        style={{
                                          position: "relative",
                                          padding: "4px 10px",
                                          borderRadius: "12px",
                                          backgroundColor:
                                            tradeGrade === "Low Risk"
                                              ? theme[0] === "dark"
                                                ? "rgba(38, 166, 93, 0.15)"
                                                : "rgba(38, 166, 93, 0.1)"
                                              : tradeGrade === "High Risk"
                                                ? theme[0] === "dark"
                                                  ? "rgba(255, 165, 0, 0.15)"
                                                  : "rgba(255, 165, 0, 0.1)"
                                                : tradeGrade === "Invalid"
                                                  ? theme[0] === "dark"
                                                    ? "rgba(242, 54, 69, 0.15)"
                                                    : "rgba(242, 54, 69, 0.1)"
                                                  : theme[0] === "dark"
                                                    ? "rgba(242, 54, 69, 0.15)"
                                                    : "rgba(242, 54, 69, 0.1)",
                                          border: `2px solid ${
                                            tradeGrade === "Low Risk"
                                              ? "#26a65d"
                                              : tradeGrade === "High Risk"
                                                ? "#FFA500"
                                                : tradeGrade === "Invalid"
                                                  ? "#f23645"
                                                  : "#f23645"
                                          }`,
                                          minWidth:
                                            width < 768 ? "100px" : "120px",
                                          maxWidth:
                                            width < 768 ? "140px" : "160px",
                                          textAlign: "center",
                                        }}
                                      >
                                        <p
                                          style={{
                                            fontSize: "8px",
                                            fontWeight: "600",
                                            letterSpacing: "0.8px",
                                            color:
                                              theme[0] !== "dark"
                                                ? "#6b7280"
                                                : "#9ca3af",
                                            margin: "0 0 2px 0",
                                          }}
                                        >
                                          TRADE RISK
                                        </p>
                                        <p
                                          style={{
                                            fontSize: "12px",
                                            fontWeight: "600",
                                            letterSpacing: "0.3px",
                                            margin: 0,
                                            color:
                                              tradeGrade === "Low Risk"
                                                ? "#26a65d"
                                                : tradeGrade === "High Risk"
                                                  ? "#FFA500"
                                                  : tradeGrade === "Invalid"
                                                    ? "#f23645"
                                                    : "#f23645",
                                          }}
                                        >
                                          {tradeGrade
                                            .replace(" Risk", "")
                                            .toUpperCase()}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  (() => {
                                    const grade = tradeGrade || forecastGrade;
                                    return (
                                      grade && (
                                        <div
                                          style={{
                                            position: "relative",
                                            padding: "4px 10px",
                                            borderRadius: "12px",
                                            backgroundColor:
                                              grade === "Low Risk"
                                                ? theme[0] === "dark"
                                                  ? "rgba(38, 166, 93, 0.15)"
                                                  : "rgba(38, 166, 93, 0.1)"
                                                : grade === "High Risk"
                                                  ? theme[0] === "dark"
                                                    ? "rgba(255, 165, 0, 0.15)"
                                                    : "rgba(255, 165, 0, 0.1)"
                                                  : grade === "Invalid"
                                                    ? theme[0] === "dark"
                                                      ? "rgba(242, 54, 69, 0.15)"
                                                      : "rgba(242, 54, 69, 0.1)"
                                                    : theme[0] === "dark"
                                                      ? "rgba(242, 54, 69, 0.15)"
                                                      : "rgba(242, 54, 69, 0.1)",
                                            border: `2px solid ${
                                              grade === "Low Risk"
                                                ? "#26a65d"
                                                : grade === "High Risk"
                                                  ? "#FFA500"
                                                  : grade === "Invalid"
                                                    ? "#f23645"
                                                    : "#f23645"
                                            }`,
                                            minWidth:
                                              width < 768 ? "100px" : "120px",
                                            maxWidth:
                                              width < 768 ? "140px" : "160px",
                                            textAlign: "center",
                                          }}
                                        >
                                          <p
                                            style={{
                                              fontSize: "8px",
                                              fontWeight: "600",
                                              letterSpacing: "0.8px",
                                              color:
                                                theme[0] !== "dark"
                                                  ? "#6b7280"
                                                  : "#9ca3af",
                                              margin: "0 0 2px 0",
                                            }}
                                          >
                                            RISK GRADE
                                          </p>
                                          <p
                                            style={{
                                              fontSize: "12px",
                                              fontWeight: "600",
                                              letterSpacing: "0.3px",
                                              margin: 0,
                                              color:
                                                grade === "Low Risk"
                                                  ? "#26a65d"
                                                  : grade === "High Risk"
                                                    ? "#FFA500"
                                                    : grade === "Invalid"
                                                      ? "#f23645"
                                                      : "#f23645",
                                            }}
                                          >
                                            {grade
                                              .replace(" Risk", "")
                                              .toUpperCase()}
                                          </p>
                                        </div>
                                      )
                                    );
                                  })()
                                )}
                              </>
                            );
                          })()}
                      </div>
                    );
                  })()}
                </div>
              </Grid>

              {!accordion && (
                <>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ padding: { xs: "0", md: "8px" } }}
                  >
                    <div
                      style={{
                        cursor: "pointer",
                        padding: "10px 16px",
                        margin: "0 auto 16px auto",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `2px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                        textAlign: "center",
                        maxWidth: "160px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: getTitle(8).isEntryTimeframe
                            ? theme[0] === "dark"
                              ? "#ED9E0E"
                              : "#d97706"
                            : theme[0] !== "dark"
                              ? "#1a1b1e"
                              : "#FCFCFF",
                          margin: 0,
                        }}
                      >
                        {getTitle(8).title}
                      </p>
                    </div>
                    {pairSelected?.daily_before ? (
                      <>
                        <img
                          style={{
                            width: "100%",
                            height: "430px",
                            objectFit: "cover",
                            borderRadius: "10px",
                            cursor: "pointer",
                          }}
                          src={pairSelected?.daily_before}
                          alt={pairSelected?.daily_before}
                          height="auto"
                          onClick={() => openImageViewer(8)}
                        />
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "16px",
                            fontWeight: "300",
                          }}
                        >
                          {formatDateTimeInTimezone(
                            pairSelected.entryDate,
                            pairSelected.entryTime,
                            pairSelected.timezone,
                          )}
                        </p>
                      </>
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          fontSize: "15px",
                          color: "#f23645",
                          fontWeight: "300",
                        }}
                      >
                        No Screenshot Detected
                      </p>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ padding: { xs: "0", md: "8px" } }}
                  >
                    <div
                      style={{
                        cursor: "pointer",
                        padding: "10px 16px",
                        margin: "0 auto 16px auto",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `2px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                        textAlign: "center",
                        maxWidth: "160px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: getTitle(5).isEntryTimeframe
                            ? theme[0] === "dark"
                              ? "#ED9E0E"
                              : "#d97706"
                            : theme[0] !== "dark"
                              ? "#1a1b1e"
                              : "#FCFCFF",
                          margin: 0,
                        }}
                      >
                        {getTitle(5).title}
                      </p>
                    </div>

                    {pairSelected?.hour1_after ? (
                      <>
                        <img
                          style={{
                            width: "100%",
                            height: "430px",
                            objectFit: "cover",
                            borderRadius: "10px",
                            cursor: "pointer",
                          }}
                          src={pairSelected?.hour1_after}
                          alt={pairSelected?.hour1_after}
                          height="auto"
                          onClick={() => openImageViewer(5)}
                        />
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "16px",
                            fontWeight: "300",
                          }}
                        >
                          {formatDateTimeInTimezone(
                            pairSelected.exitDate,
                            pairSelected.exitTime,
                            pairSelected.timezone,
                          )}
                        </p>
                      </>
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          fontSize: "15px",
                          color: "#f23645",
                          fontWeight: "300",
                        }}
                      >
                        No Screenshot Detected
                      </p>
                    )}
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                {ScreenShots(pairSelected)}
              </Grid>
              <Grid
                container
                spacing={5}
                justifyContent="space-evenly"
                alignItems="center"
                sx={{
                  minHeight: "100%",
                  fontSize: "16px",
                  order: width <= 500 ? 2 : 0,
                }}
              >
                <Grid
                  item
                  xs={12}
                  sx={{
                    marginTop: "2.5em",
                    marginBottom: "16px",
                  }}
                >
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      padding: "0",
                    }}
                  >
                    <Grid item xs={12} md={4}>
                      <StarRating
                        rating={executionDiscipline}
                        onRatingChange={setExecutionDiscipline}
                        label="Execution Discipline"
                        description=""
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <StarRating
                        rating={emotionalControl}
                        onRatingChange={setEmotionalControl}
                        label="Emotional Control"
                        description=""
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <StarRating
                        rating={setupQuality}
                        onRatingChange={setSetupQuality}
                        label="Setup Quality"
                        description=""
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid
                  ref={journalQuestionsRef}
                  item
                  xs={12}
                  sm={11}
                  sx={{
                    marginBottom: { xs: "1em", sm: "0" },
                    padding: { xs: "0", sm: "8px" },
                  }}
                >
                  <Box
                    sx={{
                      padding: { xs: "20px", sm: "24px" },
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "12px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            fontStyle: "italic",
                            margin: "0 0 4px 0",
                          }}
                        >
                          <span
                            style={{
                              color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                            }}
                          >
                            Q1)
                          </span>{" "}
                          <span
                            style={{
                              color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                            }}
                          >
                            {getJournalQuestions(useNewQuestions).question_1}
                          </span>
                        </p>
                      </div>
                      <VoiceToText onTranscript={setPros} currentValue={pros} />
                    </div>
                    {useNewQuestions ? (
                      <>
                        <p
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                            margin: "0 0 4px 0",
                          }}
                        >
                          Followed trading plan:
                        </p>
                        <CustomMultiTextField
                          size="small"
                          rows={calculateRows(executionRight)}
                          variant="standard"
                          value={executionRight}
                          label=""
                          onChange={(e) => setExecutionRight(e.target.value)}
                          disableUnderline={true}
                          fontSize={"16px"}
                        />
                        <p
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                            margin: "12px 0 4px 0",
                          }}
                        >
                          Where did I deviate:
                        </p>
                        <CustomMultiTextField
                          size="small"
                          rows={calculateRows(executionDeviate)}
                          variant="standard"
                          value={executionDeviate}
                          label=""
                          onChange={(e) => setExecutionDeviate(e.target.value)}
                          disableUnderline={true}
                          fontSize={"16px"}
                        />
                      </>
                    ) : (
                      <CustomMultiTextField
                        size="small"
                        rows={calculateRows(pros)}
                        variant="standard"
                        value={pros}
                        label=""
                        onChange={(e) => setPros(e.target.value)}
                        disableUnderline={true}
                        fontSize={"16px"}
                      />
                    )}
                  </Box>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={11}
                  sx={{
                    marginBottom: { xs: "1em", sm: "0" },
                    padding: { xs: "0", sm: "8px" },
                  }}
                >
                  <Box
                    sx={{
                      padding: { xs: "20px", sm: "24px" },
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "12px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            fontStyle: "italic",
                            margin: "0 0 4px 0",
                          }}
                        >
                          <span
                            style={{
                              color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                            }}
                          >
                            Q2)
                          </span>{" "}
                          <span
                            style={{
                              color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                            }}
                          >
                            {getJournalQuestions(useNewQuestions).question_2}
                          </span>
                        </p>
                      </div>
                      <VoiceToText onTranscript={setCons} currentValue={cons} />
                    </div>
                    {useNewQuestions ? (
                      <>
                        <p
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                            margin: "0 0 4px 0",
                          }}
                        >
                          Is it system-appropriate:
                        </p>
                        <CustomMultiTextField
                          size="small"
                          rows={calculateRows(systemAppropriate)}
                          value={systemAppropriate}
                          label=""
                          onChange={(e) => setSystemAppropriate(e.target.value)}
                          variant="standard"
                          disableUnderline={true}
                          fontSize={"16px"}
                        />
                        <p
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                            margin: "12px 0 4px 0",
                          }}
                        >
                          Why:
                        </p>
                        <CustomMultiTextField
                          size="small"
                          rows={calculateRows(systemWhy)}
                          value={systemWhy}
                          label=""
                          onChange={(e) => setSystemWhy(e.target.value)}
                          variant="standard"
                          disableUnderline={true}
                          fontSize={"16px"}
                        />
                      </>
                    ) : (
                      <CustomMultiTextField
                        size="small"
                        rows={calculateRows(cons)}
                        value={cons}
                        label=""
                        onChange={(e) => setCons(e.target.value)}
                        variant="standard"
                        disableUnderline={true}
                        fontSize={"16px"}
                      />
                    )}
                  </Box>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={11}
                  sx={{
                    marginBottom: { xs: "1em", sm: "0" },
                    padding: { xs: "0", sm: "8px" },
                  }}
                >
                  <Box
                    sx={{
                      padding: { xs: "20px", sm: "24px" },
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "12px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            fontStyle: "italic",
                            margin: "0 0 4px 0",
                          }}
                        >
                          <span
                            style={{
                              color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                            }}
                          >
                            Q3)
                          </span>{" "}
                          <span
                            style={{
                              color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                            }}
                          >
                            {getJournalQuestions(useNewQuestions).question_3}
                          </span>
                        </p>
                      </div>
                      <VoiceToText
                        onTranscript={setEmotions}
                        currentValue={emotions}
                      />
                    </div>
                    {useNewQuestions ? (
                      <>
                        <p
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                            margin: "0 0 4px 0",
                          }}
                        >
                          Before entry:
                        </p>
                        <CustomMultiTextField
                          size="small"
                          rows={calculateRows(emotionsBefore)}
                          variant="standard"
                          value={emotionsBefore}
                          label=""
                          onChange={(e) => setEmotionsBefore(e.target.value)}
                          disableUnderline={true}
                          fontSize={"16px"}
                        />
                        <p
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                            margin: "12px 0 4px 0",
                          }}
                        >
                          While active:
                        </p>
                        <CustomMultiTextField
                          size="small"
                          rows={calculateRows(emotionsWhile)}
                          variant="standard"
                          value={emotionsWhile}
                          label=""
                          onChange={(e) => setEmotionsWhile(e.target.value)}
                          disableUnderline={true}
                          fontSize={"16px"}
                        />
                        <p
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                            margin: "12px 0 4px 0",
                          }}
                        >
                          After exit:
                        </p>
                        <CustomMultiTextField
                          size="small"
                          rows={calculateRows(emotionsAfter)}
                          variant="standard"
                          value={emotionsAfter}
                          label=""
                          onChange={(e) => setEmotionsAfter(e.target.value)}
                          disableUnderline={true}
                          fontSize={"16px"}
                        />
                      </>
                    ) : (
                      <CustomMultiTextField
                        size="small"
                        rows={calculateRows(emotions)}
                        variant="standard"
                        value={emotions}
                        label=""
                        onChange={(e) => setEmotions(e.target.value)}
                        disableUnderline={true}
                        fontSize={"16px"}
                      />
                    )}
                  </Box>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={11}
                  sx={{
                    marginBottom: { xs: "1em", sm: "0" },
                    padding: { xs: "0", sm: "8px" },
                  }}
                >
                  <Box
                    sx={{
                      padding: { xs: "20px", sm: "24px" },
                      borderRadius: "12px",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(45, 46, 51, 0.4)"
                          : "rgba(249, 250, 251, 0.8)",
                      border: `1px solid ${
                        theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                      }`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "12px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            fontStyle: "italic",
                            margin: "0 0 4px 0",
                          }}
                        >
                          <span
                            style={{
                              color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                            }}
                          >
                            Q4)
                          </span>{" "}
                          <span
                            style={{
                              color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                            }}
                          >
                            {getJournalQuestions(useNewQuestions).question_4}
                          </span>
                        </p>
                      </div>
                      <VoiceToText
                        onTranscript={setTakeAgain}
                        currentValue={takeAgain}
                      />
                    </div>
                    {useNewQuestions ? (
                      <CustomMultiTextField
                        size="small"
                        rows={calculateRows(takeAgain)}
                        variant="standard"
                        value={takeAgain}
                        label=""
                        onChange={(e) => setTakeAgain(e.target.value)}
                        disableUnderline={true}
                        fontSize={"16px"}
                      />
                    ) : (
                      <CustomMultiTextField
                        size="small"
                        rows={calculateRows(takeAgain)}
                        variant="standard"
                        value={takeAgain}
                        label=""
                        onChange={(e) => setTakeAgain(e.target.value)}
                        disableUnderline={true}
                        fontSize={"16px"}
                      />
                    )}
                  </Box>
                </Grid>

                {!useNewQuestions && (
                  <>
                    <Grid
                      item
                      xs={12}
                      sm={11}
                      sx={{
                        marginBottom: { xs: "1em", sm: "0" },
                        padding: { xs: "0", sm: "8px" },
                      }}
                    >
                      <Box
                        sx={{
                          padding: { xs: "20px", sm: "24px" },
                          borderRadius: "12px",
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(45, 46, 51, 0.4)"
                              : "rgba(249, 250, 251, 0.8)",
                          border: `1px solid ${
                            theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                          }`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: "12px",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <p
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                fontStyle: "italic",
                                margin: "0 0 4px 0",
                              }}
                            >
                              <span
                                style={{
                                  color:
                                    theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                                }}
                              >
                                Q5)
                              </span>{" "}
                              <span
                                style={{
                                  color:
                                    theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                                }}
                              >
                                {getJournalQuestions(useNewQuestions).question_5}
                              </span>
                            </p>
                          </div>
                          <VoiceToText
                            onTranscript={setPlan}
                            currentValue={plan}
                          />
                        </div>
                        <CustomMultiTextField
                          size="small"
                          rows={calculateRows(plan)}
                          variant="standard"
                          value={plan}
                          label=""
                          onChange={(e) => setPlan(e.target.value)}
                          disableUnderline={true}
                          fontSize={"16px"}
                        />
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={11}
                      sx={{
                        marginBottom: { xs: "1em", sm: "0" },
                        padding: { xs: "0", sm: "8px" },
                      }}
                    >
                      <Box
                        sx={{
                          padding: { xs: "20px", sm: "24px" },
                          borderRadius: "12px",
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(45, 46, 51, 0.4)"
                              : "rgba(249, 250, 251, 0.8)",
                          border: `1px solid ${
                            theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                          }`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: "12px",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <p
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                fontStyle: "italic",
                                margin: "0 0 4px 0",
                              }}
                            >
                              <span
                                style={{
                                  color:
                                    theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                                }}
                              >
                                Q6)
                              </span>{" "}
                              <span
                                style={{
                                  color:
                                    theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                                }}
                              >
                                {getJournalQuestions(useNewQuestions).question_6}
                              </span>
                            </p>
                          </div>
                          <VoiceToText
                            onTranscript={setExtraNotes}
                            currentValue={extraNotes}
                          />
                        </div>
                        <CustomMultiTextField
                          size="small"
                          rows={calculateRows(extraNotes)}
                          variant="standard"
                          value={extraNotes}
                          label=""
                          onChange={(e) => setExtraNotes(e.target.value)}
                          disableUnderline={true}
                          fontSize={"16px"}
                        />
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>

              <Grid
                xs={12}
                item
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  margin: "2em 0 1em 0",
                  order: { xs: 2, sm: 0 },
                }}
              >
                <div style={{ maxWidth: "500px", margin: "0em auto 2em auto" }}>
                  {!linkedForecast || linkedForecast === "" ? (
                    <>
                      {showForecastSelect && (
                        <div
                          style={{
                            minWidth: "250px",
                            marginBottom: "1em",
                            padding: "16px",
                            borderRadius: "16px",
                            backgroundColor:
                              theme[0] === "dark"
                                ? "rgba(45, 46, 51, 0.6)"
                                : "rgba(249, 250, 251, 0.9)",
                            border: `2px solid ${
                              theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                            }`,
                          }}
                        >
                          <CustomSelect
                            size="small"
                            defaultValue=""
                            placeholder="Forecasts To Link"
                            value={linkedForecast ? linkedForecast : ""}
                            onChange={(event) => {
                              setLinkedForecast(event.target.value);
                              setShowForecastSelect(false);
                            }}
                            menuItemArray={
                              auth.backtestingMode
                                ? forecastsBacktestingMenuItem
                                : forecastsMenuItem
                            }
                          />
                        </div>
                      )}
                      <div
                        onClick={() =>
                          setShowForecastSelect(!showForecastSelect)
                        }
                        style={{
                          margin: showForecastSelect
                            ? "1em auto 0 auto"
                            : "0 auto",
                          padding: "12px 20px",
                          borderRadius: "20px",
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(237, 158, 14, 0.12)"
                              : "rgba(237, 158, 14, 0.08)",
                          border: "2px solid #ED9E0E",
                          maxWidth: "500px",
                          textAlign: "center",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            theme[0] === "dark"
                              ? "rgba(237, 158, 14, 0.2)"
                              : "rgba(237, 158, 14, 0.15)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor =
                            theme[0] === "dark"
                              ? "rgba(237, 158, 14, 0.12)"
                              : "rgba(237, 158, 14, 0.08)";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        <span
                          style={{
                            color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                            fontSize: "12px",
                            fontWeight: "600",
                            letterSpacing: "0.5px",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          LINKED FORECAST
                        </span>
                        <span
                          style={{
                            color: "#ED9E0E",
                            fontSize: "16px",
                            fontWeight: "600",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {showForecastSelect ? "Close" : "No Linked Forecast"}
                        </span>
                      </div>
                    </>
                  ) : (
                    <Tooltip
                      title="Hold down to remove linked forecast"
                      placement="top"
                    >
                      <div
                        onClick={() => {
                          if (linkedForecast && linkedForecast !== "") {
                            localStorage.setItem(
                              "forecast-to-view",
                              linkedForecast,
                            );
                            localStorage.removeItem("trade-portal");
                            localStorage.removeItem("journal-portal");
                            auth.setTab(0);
                            onClose();
                          }
                        }}
                        style={{
                          margin: "0 auto",
                          padding: "12px 20px",
                          borderRadius: "20px",
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(38, 166, 93, 0.12)"
                              : "rgba(38, 166, 93, 0.08)",
                          border: "2px solid #26a65d",
                          maxWidth: "500px",
                          textAlign: "center",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            theme[0] === "dark"
                              ? "rgba(38, 166, 93, 0.2)"
                              : "rgba(38, 166, 93, 0.08)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor =
                            theme[0] === "dark"
                              ? "rgba(38, 166, 93, 0.12)"
                              : "rgba(38, 166, 93, 0.08)";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        <span
                          {...bind()}
                          id="setLinkedForecast"
                          style={{
                            color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                            fontSize: "12px",
                            fontWeight: "600",
                            letterSpacing: "0.5px",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          LINKED FORECAST
                        </span>
                        <span
                          style={{
                            color: "#26a65d",
                            fontSize: "16px",
                            fontWeight: "600",
                            letterSpacing: "0.5px",
                          }}
                        >
                          View Forecast
                        </span>
                      </div>
                    </Tooltip>
                  )}
                </div>
              </Grid>

              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "16px",
                  margin: linkedForecast ? "0 0 1.7em 0" : "1em 0 1.7em 0",
                  order: { xs: 3, sm: 1 },
                  flexDirection: "row",
                  alignItems: "center",
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                }}
              >
                <Tooltip title="Close">
                  <div
                    onClick={() => goBack()}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "12px 18px",
                      borderRadius: "24px",
                      cursor: "pointer",
                      backgroundColor:
                        theme[0] === "dark"
                          ? "rgba(156, 163, 175, 0.12)"
                          : "rgba(107, 114, 128, 0.08)",
                      border: `2px solid ${
                        theme[0] === "dark" ? "#9ca3af" : "#6b7280"
                      }`,
                      transition: "all 0.3s ease",
                      width: "auto",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        theme[0] === "dark"
                          ? "rgba(156, 163, 175, 0.2)"
                          : "rgba(107, 114, 128, 0.15)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        theme[0] === "dark"
                          ? "rgba(156, 163, 175, 0.12)"
                          : "rgba(107, 114, 128, 0.08)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <CloseIcon
                      sx={{
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        fontSize: "24px",
                      }}
                    />
                    <span
                      style={{
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        fontWeight: "600",
                        fontSize: "14px",
                        letterSpacing: "0.5px",
                        display: width < 768 ? "none" : "inline",
                      }}
                    >
                      CLOSE
                    </span>
                  </div>
                </Tooltip>

                <Tooltip title={isSharing ? "Generating..." : "Share Journal"}>
                  <div
                    onClick={handleShare}
                    onMouseEnter={() => setIsHoveringShare(true)}
                    onMouseLeave={() => setIsHoveringShare(false)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "12px 18px",
                      borderRadius: "24px",
                      cursor: isSharing ? "not-allowed" : "pointer",
                      backgroundColor:
                        isHoveringShare || isSharing
                          ? theme[0] === "dark"
                            ? "rgba(59, 130, 246, 0.15)"
                            : "rgba(59, 130, 246, 0.1)"
                          : theme[0] === "dark"
                            ? "rgba(156, 163, 175, 0.12)"
                            : "rgba(107, 114, 128, 0.08)",
                      border: `2px solid ${
                        isHoveringShare || isSharing
                          ? "#3B82F6"
                          : theme[0] === "dark"
                            ? "#9ca3af"
                            : "#6b7280"
                      }`,
                      color:
                        isHoveringShare || isSharing
                          ? "#3B82F6"
                          : theme[0] === "dark"
                            ? "#9ca3af"
                            : "#6b7280",
                      transition: "all 0.3s ease",
                      opacity: isSharing ? 0.6 : 1,
                      width: "auto",
                      justifyContent: "center",
                    }}
                  >
                    <ShareIcon sx={{ fontSize: "20px" }} />
                    <span
                      style={{
                        color:
                          isHoveringShare || isSharing
                            ? "#3B82F6"
                            : theme[0] === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                        fontWeight: "600",
                        fontSize: "14px",
                        letterSpacing: "0.5px",
                        display: width < 768 ? "none" : "inline",
                      }}
                    >
                      {isSharing ? "GENERATING..." : "SHARE"}
                    </span>
                  </div>
                </Tooltip>

                <Tooltip title={formSubmitted ? "Saving..." : "Save Changes"}>
                  <div
                    onClick={() => {
                      if (!formSubmitted) {
                        const journal = JSON.parse(
                          localStorage.getItem("journal-portal"),
                        );
                        const trade = JSON.parse(
                          localStorage.getItem("trade-portal"),
                        );
                        editJournal({
                          trade_id: journal.trade_id,
                          journalID: journal.journalID,
                          pros: pros,
                          cons: cons,
                          emotions: emotions,
                          plan: plan,
                          takeAgain: takeAgain,
                          extraNotes: extraNotes,
                          tradeToUpdate: trade,
                          dxyHourlyBeforeNote: dxyHourlyBeforeNote,
                          dxyHourlyAfterNote: dxyHourlyAfterNote,
                          dxyDailyBeforeNote: dxyDailyBeforeNote,
                          dxyDailyAfterNote: dxyDailyAfterNote,
                          min5_beforeNote: min5_beforeNote,
                          min15_beforeNote: min15_beforeNote,
                          min5_afterNote: min5_afterNote,
                          min15_afterNote: min15_afterNote,
                          oneHour_beforeNote: oneHour_beforeNote,
                          oneHour_afterNote: oneHour_afterNote,
                          fourHour_afterNote: fourHour_afterNote,
                          fourHour_beforeNote: fourHour_beforeNote,
                          daily_beforeNote: daily_beforeNote,
                          daily_afterNote: daily_afterNote,
                          weekly_beforeNote: weekly_beforeNote,
                          weekly_afterNote: weekly_afterNote,
                          executionDiscipline: executionDiscipline,
                          emotionalControl: emotionalControl,
                          setupQuality: setupQuality,
                          executionRight: executionRight,
                          executionDeviate: executionDeviate,
                          systemAppropriate: systemAppropriate,
                          systemWhy: systemWhy,
                          emotionsBefore: emotionsBefore,
                          emotionsWhile: emotionsWhile,
                          emotionsAfter: emotionsAfter,
                        });
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "12px 18px",
                      borderRadius: "24px",
                      cursor: formSubmitted ? "not-allowed" : "pointer",
                      backgroundColor:
                        theme[0] === "dark"
                          ? formSubmitted
                            ? "rgba(156, 163, 175, 0.12)"
                            : "rgba(38, 166, 93, 0.12)"
                          : formSubmitted
                            ? "rgba(107, 114, 128, 0.08)"
                            : "rgba(38, 166, 93, 0.08)",
                      border: `2px solid ${
                        formSubmitted ? "#9ca3af" : "#26a65d"
                      }`,
                      transition: "all 0.3s ease",
                      opacity: formSubmitted ? 0.6 : 1,
                      width: "auto",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      if (!formSubmitted) {
                        e.currentTarget.style.backgroundColor =
                          theme[0] === "dark"
                            ? "rgba(38, 166, 93, 0.2)"
                            : "rgba(38, 166, 93, 0.15)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!formSubmitted) {
                        e.currentTarget.style.backgroundColor =
                          theme[0] === "dark"
                            ? "rgba(38, 166, 93, 0.12)"
                            : "rgba(38, 166, 93, 0.08)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }
                    }}
                  >
                    <SaveIcon
                      sx={{
                        color: formSubmitted ? "#9ca3af" : "#26a65d",
                        fontSize: "24px",
                      }}
                    />
                    <span
                      style={{
                        color: formSubmitted ? "#9ca3af" : "#26a65d",
                        fontWeight: "600",
                        fontSize: "14px",
                        letterSpacing: "0.5px",
                        display: width < 768 ? "none" : "inline",
                      }}
                    >
                      SAVE
                    </span>
                  </div>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>

          {accordion &&
            createPortal(
              <Tooltip
                title={hasJumpedToQuestions ? "Jump Back" : "Journal Questions"}
              >
                <div
                  onClick={
                    hasJumpedToQuestions
                      ? scrollToPreviousPosition
                      : scrollToJournalQuestions
                  }
                  style={{
                    position: "fixed",
                    bottom: "20px",
                    right: "20px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.95)"
                        : "rgba(255, 255, 255, 0.95)",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    borderRadius: "50%",
                    width: "56px",
                    height: "56px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                    zIndex: 99999,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.1)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 24px rgba(0, 0, 0, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 20px rgba(0, 0, 0, 0.3)";
                  }}
                >
                  {hasJumpedToQuestions ? (
                    <ArrowUpwardIcon
                      style={{
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        fontSize: "28px",
                      }}
                    />
                  ) : (
                    <ArrowDownwardIcon
                      style={{
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        fontSize: "28px",
                      }}
                    />
                  )}
                </div>
              </Tooltip>,
              document.body,
            )}

          <ConfirmDialog
            open={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={confirmDeleteScreenshot}
            title="Delete Screenshot"
            message="Are you sure you want to delete this screenshot?"
          />
        </>
      </Modal>
    )
  );
};

export default Journal;
